/* Appligator Office route shell: read-only global Action Center debugger. */
(function () {
  const { useCallback, useEffect, useMemo, useRef, useState } = React;
  const Icon = window.Icon || (() => null);

  function norm(value) { return String(value || "").trim().toLowerCase(); }
  function dateTimeLabel(value) {
    if (!value) return "Not loaded";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Not loaded" : date.toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }
  function hasValue(value) {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  }
  function ageLabel(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    return `${Math.round(minutes / 60)}h`;
  }
  function isOfficeRefreshChange(change) {
    const toolName = norm(change && change.toolName);
    if (toolName.startsWith("action-center/")) return true;
    if (change && change.kind === "employee-activity" && change.jobId) return true;
    if (change && change.kind === "tool-call" && change.aiEmployeeId) return true;
    return false;
  }
  function officeChangeFromChange(change) {
    const details = change && change.details;
    const officeChange = details && details.officeChange;
    if (!officeChange || officeChange.contractVersion !== "office-action-center.v1") return null;
    return officeChange;
  }
  function changedAtMillis(value) {
    const millis = Date.parse(value || "");
    return Number.isFinite(millis) ? millis : 0;
  }
  function officeChangeRunKey(officeChange) {
    return String((officeChange && officeChange.runId) || (officeChange && officeChange.workItem && officeChange.workItem.runId) || "");
  }
  function officeChangeIsNewer(previous, officeChange) {
    if (!previous) return true;
    const nextVersion = Number(officeChange && officeChange.version) || 0;
    const previousVersion = Number(previous.version) || 0;
    if (nextVersion !== previousVersion) return nextVersion > previousVersion;
    return changedAtMillis(officeChange && officeChange.changedAt) >= changedAtMillis(previous.changedAt);
  }
  function officeWorkReference(item) {
    if (!item) return null;
    const timing = item.timing || {};
    const compression = item.compression || {};
    return {
      runId: item.runId,
      state: item.state || "",
      status: item.status || "",
      category: item.category || (item.semanticEvent && item.semanticEvent.category) || "",
      title: workTitle(item),
      updatedAt: latestWorkTime(item) || timing.updatedAt || timing.createdAt || "",
      isCompressed: !!compression.isCompressed,
      compressedCount: Number(compression.compressedCount) || 0,
      animationIntent: item.animationIntent || null,
    };
  }
  function employeeStateFromWorkItem(item) {
    const state = norm(item && (item.state || item.status));
    const fieldStatus = officeFieldStateStatus(item && item.fieldStates);
    if (state.includes("restricted") || state.includes("denied") || state.includes("permission") || fieldStatus === "restricted") return "restricted";
    if (state.includes("stale") || fieldStatus === "stale") return "stale";
    if (state.includes("missing") || state.includes("unavailable") || fieldStatus === "missing") return "missing";
    if (state.includes("loading") || state.includes("pending") || fieldStatus === "loading") return "loading";
    if (state.includes("fail") || state.includes("error")) return "failed";
    if (state.includes("block") || state.includes("need")) return "blocked";
    if (state.includes("queue")) return "queued";
    if (state.includes("delegat")) return "delegated";
    if (state.includes("complete") || state.includes("done")) return "completed";
    if (state.includes("run") || state.includes("work") || state.includes("progress")) return "running";
    return item ? (item.state || item.status || "running") : "idle";
  }
  function mergeOfficeWorkItemIntoSnapshot(snapshot, officeChange) {
    const workItem = officeChange && officeChange.workItem;
    const runId = workRunId(workItem);
    const employeeId = workItem && workItem.employeeId;
    if (!snapshot || !workItem || !runId || !employeeId) return null;

    let employeeFound = false;
    const work = Array.isArray(snapshot.work) ? snapshot.work.slice() : [];
    const workIndex = work.findIndex((item) => String(workRunId(item)) === String(runId));
    if (workIndex >= 0) work[workIndex] = { ...work[workIndex], ...workItem };
    else work.push(workItem);
    work.sort((a, b) => new Date(latestWorkTime(b) || 0) - new Date(latestWorkTime(a) || 0));

    const reference = officeWorkReference(workItem);
    const employees = (Array.isArray(snapshot.employees) ? snapshot.employees : []).map((employee) => {
      if (String(employee && employee.id) !== String(employeeId)) return employee;
      employeeFound = true;
      const recentHistory = Array.isArray(employee.recentHistory) ? employee.recentHistory.filter((item) => String(item && item.runId) !== String(runId)) : [];
      const activeWorkCount = work.filter((item) => String(item.employeeId) === String(employeeId) && IsActiveWork(item)).length;
      return {
        ...employee,
        state: employeeStateFromWorkItem(workItem),
        currentRunId: runId,
        currentWorkTitle: workTitle(workItem),
        queueCount: Math.max(Number(employee.queueCount) || 0, activeWorkCount),
        lastActivityAt: latestWorkTime(workItem) || employee.lastActivityAt,
        currentWork: reference,
        recentHistory: [reference, ...recentHistory].filter(Boolean).slice(0, 12),
      };
    });
    if (!employeeFound) return null;

    const previousCaps = snapshot.caps || {};
    const generatedAt = (officeChange && officeChange.changedAt) || new Date().toISOString();
    return {
      ...snapshot,
      generatedAt,
      freshness: {
        ...(snapshot.freshness || {}),
        state: "live",
        lastRunUpdatedAt: (officeChange && officeChange.changedAt) || (snapshot.freshness && snapshot.freshness.lastRunUpdatedAt) || snapshot.generatedAt,
        reason: "Updated from the live Office change stream.",
      },
      caps: {
        ...previousCaps,
        includedWorkCount: work.length,
      },
      employees,
      work,
    };
  }
  function permissionDenied(error) {
    const message = norm(error && error.message);
    return error && (error.status === 401 || error.status === 403) || message.includes("authentication required") || message.includes("forbidden") || message.includes("not authorized") || message.includes("permission");
  }
  function employeeStateLabel(state) {
    const clean = norm(state);
    if (!clean) return "Unknown";
    return clean.split(/[-_\s]+/).map((part) => part ? part.charAt(0).toUpperCase() + part.slice(1) : "").join(" ");
  }
  function workStateClass(item) {
    const state = norm(item && (item.state || item.status || item.representativeStatus));
    const fieldStatus = officeFieldStateStatus(item && item.fieldStates);
    if (state.includes("restricted") || state.includes("denied") || state.includes("permission") || fieldStatus === "restricted") return "restricted";
    if (state.includes("stale") || fieldStatus === "stale") return "stale";
    if (state.includes("missing") || state.includes("unavailable") || fieldStatus === "missing") return "missing";
    if (state.includes("loading") || state.includes("pending") || fieldStatus === "loading") return "loading";
    if (state.includes("fail") || state.includes("error")) return "failed";
    if (state.includes("block") || state.includes("need")) return "blocked";
    if (state.includes("queue")) return "queued";
    if (state.includes("done") || state.includes("complete")) return "done";
    if (state.includes("delegat")) return "delegated";
    if (state.includes("run") || state.includes("work")) return "working";
    return "idle";
  }
  function scopeLabel(snapshot) {
    const scope = snapshot && snapshot.scope;
    if (!scope) return "All projects";
    if (scope.includesAllProjects) return scope.label || "All projects";
    return scope.label || "Project scope";
  }
  function employeeStateClass(employee) {
    const state = norm(employee && employee.state);
    const current = employee && employee.currentWork;
    const status = norm(current && (current.status || current.state));
    const category = norm(current && current.category);
    const fieldStatus = officeFieldStateStatus([...(Array.isArray(employee && employee.fieldStates) ? employee.fieldStates : []), ...(Array.isArray(current && current.fieldStates) ? current.fieldStates : [])]);
    if (state.includes("restricted") || status.includes("restricted") || state.includes("denied") || status.includes("denied") || state.includes("permission") || status.includes("permission") || fieldStatus === "restricted") return "restricted";
    if (state.includes("stale") || status.includes("stale") || fieldStatus === "stale") return "stale";
    if (state.includes("missing") || status.includes("missing") || state.includes("unavailable") || status.includes("unavailable") || fieldStatus === "missing") return "missing";
    if (state.includes("loading") || status.includes("loading") || state.includes("pending") || status.includes("pending") || fieldStatus === "loading") return "loading";
    if (state.includes("fail") || state.includes("error") || status.includes("fail") || status.includes("error")) return "failed";
    if (state.includes("block") || status.includes("block") || state.includes("need") || status.includes("need")) return "blocked";
    if (state.includes("queue") || status.includes("queue")) return "queued";
    if (state.includes("delegat") || status.includes("delegat") || category.includes("delegat")) return "delegated";
    if (state.includes("done") || state.includes("complete") || status.includes("done") || status.includes("complete")) return "completed";
    if (state.includes("run") || state.includes("work") || status.includes("run") || status.includes("progress")) return "running";
    if (state.includes("idle") || !state) return "idle";
    return "fallback";
  }
  const OFFICE_19TH_CENTURY_SCENE_CONFIG = Object.freeze({
    key: "19th-century-office",
    sourceImage: Object.freeze({
      documentId: "5bdfbe3a-38f6-4ba5-b46f-17defcd21b21",
      filename: "19th century office and factory.png",
    }),
    zones: Object.freeze({
      receptionDispatch: Object.freeze({ key: "reception_dispatch", zoneTone: "dispatch", label: "Reception and dispatch", propGroupKeys: ["receptionDesk", "dispatchBoard"], anchorKeys: ["actionCenterActivity"] }),
      mainOfficeDesks: Object.freeze({ key: "main_office_desks", zoneTone: "common", label: "Main office desks", propGroupKeys: ["woodDeskRows", "ledgerStacks"], anchorKeys: ["mainOfficeDesk", "idleAvailable"] }),
      projectRoomsCabinets: Object.freeze({ key: "project_rooms_cabinets", zoneTone: "project", label: "Project rooms and cabinets", propGroupKeys: ["projectCabinets", "contextWall"], anchorKeys: ["projectContextRead"] }),
      archiveSourceRoom: Object.freeze({ key: "archive_source_room", zoneTone: "project", label: "Archive and source room", propGroupKeys: ["archiveShelves", "sourceLedgerTable"], anchorKeys: ["sourceReview", "documentNoteEmail", "archiveSource"] }),
      ticketPlanningTable: Object.freeze({ key: "ticket_planning_table", zoneTone: "project", label: "Ticket and planning table", propGroupKeys: ["ticketPlanningTable", "paperLedgerStacks"], anchorKeys: ["ticketWorkPackage"] }),
      factoryMachines: Object.freeze({ key: "factory_machines", zoneTone: "floor", label: "Factory machines", propGroupKeys: ["factoryMachinery", "genericWorkbench"], anchorKeys: ["toolCall", "commandExecution", "testCheck", "genericToolWork", "restrictedMissingStale"] }),
      draftingTable: Object.freeze({ key: "drafting_table", zoneTone: "project", label: "Drafting table", propGroupKeys: ["draftingTable", "brassDrawingTools"], anchorKeys: ["planningThinking", "draftingTable"] }),
      mailTypewriterStation: Object.freeze({ key: "mail_typewriter_station", zoneTone: "project", label: "Mail and typewriter station", propGroupKeys: ["mailTypewriterStation"], anchorKeys: ["documentNoteEmail", "mailTypewriter"] }),
      commonArea: Object.freeze({ key: "common_area", zoneTone: "common", label: "Common area", propGroupKeys: ["availabilityBench", "completedStampTray"], anchorKeys: ["completed", "idleAvailable"] }),
      handoffPathPoints: Object.freeze({ key: "handoff_path_points", zoneTone: "floor", label: "Handoff path points", propGroupKeys: ["handoffLane", "handoffFolderCard", "handoffOutboxTray", "handoffStampBoard"], anchorKeys: ["delegation"], pathPointKeys: ["dispatch_to_factory", "factory_to_project_rooms", "project_rooms_to_archive", "archive_to_common_area"] }),
    }),
    propGroups: Object.freeze({
      receptionDesk: Object.freeze({ key: "reception_desk", zoneKey: "reception_dispatch", visualTokenKeys: ["woodDesks", "greenTrim"] }),
      dispatchBoard: Object.freeze({ key: "dispatch_board", zoneKey: "reception_dispatch", visualTokenKeys: ["paperLedger", "brassGoldHighlights"] }),
      woodDeskRows: Object.freeze({ key: "wood_desk_rows", zoneKey: "main_office_desks", visualTokenKeys: ["woodDesks", "warmLamps"] }),
      ledgerStacks: Object.freeze({ key: "ledger_stacks", zoneKey: "main_office_desks", visualTokenKeys: ["paperLedger"] }),
      projectCabinets: Object.freeze({ key: "project_cabinets", zoneKey: "project_rooms_cabinets", visualTokenKeys: ["greenTrim", "woodDesks"] }),
      contextWall: Object.freeze({ key: "context_wall", zoneKey: "project_rooms_cabinets", visualTokenKeys: ["paperLedger", "brassGoldHighlights"] }),
      archiveShelves: Object.freeze({ key: "archive_shelves", zoneKey: "archive_source_room", visualTokenKeys: ["woodDesks", "paperLedger"] }),
      sourceLedgerTable: Object.freeze({ key: "source_ledger_table", zoneKey: "archive_source_room", visualTokenKeys: ["paperLedger", "warmLamps"] }),
      ticketPlanningTable: Object.freeze({ key: "ticket_planning_table", zoneKey: "ticket_planning_table", visualTokenKeys: ["paperLedger", "woodDesks"] }),
      paperLedgerStacks: Object.freeze({ key: "paper_ledger_stacks", zoneKey: "ticket_planning_table", visualTokenKeys: ["paperLedger"] }),
      factoryMachinery: Object.freeze({ key: "factory_machinery", zoneKey: "factory_machines", visualTokenKeys: ["machinery", "stoneFloor"] }),
      genericWorkbench: Object.freeze({ key: "generic_workbench", zoneKey: "factory_machines", visualTokenKeys: ["machinery", "woodDesks"] }),
      draftingTable: Object.freeze({ key: "drafting_table", zoneKey: "drafting_table", visualTokenKeys: ["paperLedger", "brassGoldHighlights"] }),
      brassDrawingTools: Object.freeze({ key: "brass_drawing_tools", zoneKey: "drafting_table", visualTokenKeys: ["brassGoldHighlights"] }),
      mailTypewriterStation: Object.freeze({ key: "mail_typewriter_station", zoneKey: "mail_typewriter_station", visualTokenKeys: ["paperLedger", "brassGoldHighlights"] }),
      availabilityBench: Object.freeze({ key: "availability_bench", zoneKey: "common_area", visualTokenKeys: ["woodDesks", "warmLamps"] }),
      completedStampTray: Object.freeze({ key: "completed_stamp_tray", zoneKey: "common_area", visualTokenKeys: ["completedStamp", "brassGoldHighlights"] }),
      handoffLane: Object.freeze({ key: "handoff_lane", zoneKey: "handoff_path_points", visualTokenKeys: ["stoneFloor", "warmLamps"] }),
      handoffFolderCard: Object.freeze({ key: "handoff_folder_card", zoneKey: "handoff_path_points", visualTokenKeys: ["paperLedger", "brassGoldHighlights"] }),
      handoffOutboxTray: Object.freeze({ key: "handoff_outbox_tray", zoneKey: "handoff_path_points", visualTokenKeys: ["woodDesks", "paperLedger"] }),
      handoffStampBoard: Object.freeze({ key: "handoff_stamp_board", zoneKey: "handoff_path_points", visualTokenKeys: ["completedStamp", "brassGoldHighlights"] }),
    }),
    semanticAnchors: Object.freeze({
      actionCenterActivity: Object.freeze({ key: "action_center_activity", presetKey: "action_center_activity", label: "DISPATCH", sceneLabel: "Dispatch board", icon: "activity", tone: "running", zoneTone: "dispatch", zoneKey: "reception_dispatch", targetZoneKey: "dispatch_board", targetObjectKey: "dispatch_board", propGroupKey: "dispatchBoard", objectClass: "dispatch-board", motion: { x: -9, y: 4, turn: -2 }, categoryAliases: ["action_center_activity", "action_center", "dispatch", "queue", "inbox"] }),
      mainOfficeDesk: Object.freeze({ key: "main_office_desk", zoneKey: "main_office_desks", targetZoneKey: "common_area", targetObjectKey: "main_office_desk", propGroupKey: "woodDeskRows" }),
      projectContextRead: Object.freeze({ key: "project_context_read", presetKey: "project_context_read", label: "CONTEXT", sceneLabel: "Context archive", icon: "database", tone: "running", zoneTone: "project", zoneKey: "archive_source_room", targetZoneKey: "archive", targetObjectKey: "context_archive", propGroupKey: "archiveShelves", objectClass: "context-wall", motion: { x: 6, y: -6, turn: -1 }, categoryAliases: ["project_context_read", "project_context", "project", "context", "knowledge", "source_read"] }),
      sourceReview: Object.freeze({ key: "source_review", presetKey: "source_review", label: "SOURCE", sceneLabel: "Archive magnifier", icon: "search", tone: "running", zoneTone: "project", zoneKey: "archive_source_room", targetZoneKey: "archive", targetObjectKey: "archive_bundle_magnifier", propGroupKey: "archiveShelves", objectClass: "source-review", motion: { x: 6, y: -6, turn: -1 }, categoryAliases: ["source_review", "source", "source_read", "archive_review", "document_review", "attachment_review"] }),
      archiveSource: Object.freeze({ key: "archive_source", zoneKey: "archive_source_room", targetZoneKey: "archive", targetObjectKey: "archive_shelves", propGroupKey: "archiveShelves" }),
      ticketWorkPackage: Object.freeze({ key: "ticket_work_package", presetKey: "ticket_work_package", label: "TICKETS", sceneLabel: "Ticket table", icon: "list", tone: "running", zoneTone: "project", zoneKey: "ticket_planning_table", targetZoneKey: "project_office", targetObjectKey: "ticket_work_package_table", propGroupKey: "ticketPlanningTable", objectClass: "ticket-table", motion: { x: 10, y: 4, turn: 1 }, categoryAliases: ["ticket_work_package", "ticket", "work_package", "workpackage", "implementation_ticket"] }),
      documentNoteEmail: Object.freeze({ key: "document_note_email", presetKey: "document_note_email", label: "DOCS", sceneLabel: "Envelope typewriter", icon: "mail", tone: "running", zoneTone: "project", zoneKey: "mail_typewriter_station", targetZoneKey: "inbox_outbox", targetObjectKey: "archive_mail_document_station", propGroupKey: "mailTypewriterStation", objectClass: "archive-station", motion: { x: -6, y: -5, turn: -1 }, categoryAliases: ["document_note_email", "note_document_email", "note", "document", "email", "mail", "attachment", "inbox_outbox"] }),
      noteDocumentEmail: Object.freeze({ key: "document_note_email", presetKey: "document_note_email", label: "DOCS", sceneLabel: "Envelope typewriter", icon: "mail", tone: "running", zoneTone: "project", zoneKey: "mail_typewriter_station", targetZoneKey: "inbox_outbox", targetObjectKey: "archive_mail_document_station", propGroupKey: "mailTypewriterStation", objectClass: "archive-station", motion: { x: -6, y: -5, turn: -1 }, categoryAliases: ["note_document_email"] }),
      mailTypewriter: Object.freeze({ key: "mail_typewriter", zoneKey: "mail_typewriter_station", targetZoneKey: "inbox_outbox", targetObjectKey: "mail_typewriter_station", propGroupKey: "mailTypewriterStation" }),
      toolCall: Object.freeze({ key: "tool_call", presetKey: "tool_call", label: "TOOL", sceneLabel: "Gear machine", icon: "settings", tone: "running", zoneTone: "floor", zoneKey: "factory_machines", targetZoneKey: "factory_floor", targetObjectKey: "gear_machine", propGroupKey: "factoryMachinery", objectClass: "gear-machine", motion: { x: 10, y: -3, turn: 2 }, categoryAliases: ["tool_call", "tool", "tool_use", "tool_work"] }),
      commandExecution: Object.freeze({ key: "command_execution", presetKey: "command_execution", label: "PRESS", sceneLabel: "Mechanical press terminal", icon: "terminal", tone: "running", zoneTone: "floor", zoneKey: "factory_machines", targetZoneKey: "factory_floor", targetObjectKey: "mechanical_press_terminal", propGroupKey: "factoryMachinery", objectClass: "mechanical-press", motion: { x: 12, y: -4, turn: 2 }, categoryAliases: ["command_execution", "command", "terminal", "shell", "exec"] }),
      testCheck: Object.freeze({ key: "test_check", presetKey: "test_check", label: "CHECK", sceneLabel: "Gauge check slip", icon: "check", tone: "running", zoneTone: "floor", zoneKey: "factory_machines", targetZoneKey: "factory_floor", targetObjectKey: "gauge_check_slip", propGroupKey: "factoryMachinery", objectClass: "gauge-check", motion: { x: 8, y: -2, turn: 1 }, categoryAliases: ["test_check", "test", "verification", "check", "build_check"] }),
      planningThinking: Object.freeze({ key: "planning_thinking", presetKey: "planning_thinking", label: "PLAN", sceneLabel: "Lamp notebook compass", icon: "bookOpen", tone: "running", zoneTone: "project", zoneKey: "drafting_table", targetZoneKey: "project_office", targetObjectKey: "lamp_notebook_compass", propGroupKey: "draftingTable", objectClass: "planning-lamp", motion: { x: 7, y: 4, turn: 1 }, categoryAliases: ["planning_thinking", "planning", "thinking", "plan", "reading_context", "checking_files"] }),
      genericToolWork: Object.freeze({ key: "generic_tool_work", presetKey: "generic_tool_work", label: "GENERIC", sceneLabel: "Generic workbench", icon: "tool", tone: "fallback", zoneTone: "floor", zoneKey: "factory_machines", targetZoneKey: "factory_floor", targetObjectKey: "generic_terminal_workbench", propGroupKey: "genericWorkbench", objectClass: "generic-workbench", motion: { x: 10, y: -3, turn: 2 }, categoryAliases: ["generic_tool_work", "generic", "fallback", "unknown", "tool_work"] }),
      draftingTable: Object.freeze({ key: "drafting_table", zoneKey: "drafting_table", targetZoneKey: "project_office", targetObjectKey: "drafting_table", propGroupKey: "draftingTable" }),
      delegation: Object.freeze({ key: "delegation", presetKey: "delegation", label: "HANDOFF", sceneLabel: "Handoff lane", icon: "handover", tone: "delegated", zoneTone: "floor", zoneKey: "handoff_path_points", targetZoneKey: "handoff", targetObjectKey: "handoff_lane", propGroupKey: "handoffLane", objectClass: "handoff-lane", motion: { x: 12, y: 2, turn: 3 }, categoryAliases: ["delegation", "delegated", "handoff"] }),
      blocked: Object.freeze({ key: "blocked", presetKey: "blocked", label: "BLOCKED", sceneLabel: "Blocked overlay", icon: "alert", tone: "blocked", zoneTone: "state", zoneKey: "factory_machines", targetZoneKey: "inspection", targetObjectKey: "blocked_overlay_badge", visualTokenKey: "blockedFailed", objectClass: "blocked-overlay", motion: { x: 0, y: -6, turn: 0 }, nonDroppable: true, categoryAliases: ["blocked", "needs_input"] }),
      failed: Object.freeze({ key: "failed", presetKey: "failed", label: "FAILED", sceneLabel: "Failed overlay", icon: "alert", tone: "failed", zoneTone: "state", zoneKey: "factory_machines", targetZoneKey: "inspection", targetObjectKey: "failed_overlay_badge", visualTokenKey: "blockedFailed", objectClass: "failed-overlay", motion: { x: 0, y: -7, turn: 0 }, nonDroppable: true, categoryAliases: ["failed", "failure", "error"] }),
      completed: Object.freeze({ key: "completed", presetKey: "completed", label: "RECENT", sceneLabel: "Completed badge", icon: "check", tone: "completed", zoneTone: "common", zoneKey: "common_area", targetZoneKey: "recent_history", targetObjectKey: "completed_badge", propGroupKey: "completedStampTray", visualTokenKey: "completedStamp", objectClass: "completed-badge", motion: { x: 0, y: 3, turn: 0 }, categoryAliases: ["completed", "complete", "done", "recent"] }),
      idleAvailable: Object.freeze({ key: "idle_available", presetKey: "idle_available", label: "AVAILABLE", sceneLabel: "Availability bench", icon: "clock", tone: "idle", zoneTone: "common", zoneKey: "common_area", targetZoneKey: "common_area", targetObjectKey: "idle_availability", propGroupKey: "availabilityBench", objectClass: "availability-bench", motion: { x: 0, y: 2, turn: 0 }, ambientOnly: true, categoryAliases: ["idle_available", "idle", "available", "waiting"] }),
      restrictedMissingStale: Object.freeze({ key: "restricted_missing_stale", presetKey: "restricted_missing_stale", label: "LIMIT", sceneLabel: "Lock question clock", icon: "shield", tone: "fallback", zoneTone: "state", zoneKey: "factory_machines", targetZoneKey: "inspection", targetObjectKey: "lock_question_clock", visualTokenKey: "staleRestricted", objectClass: "restricted-missing-stale", motion: { x: 0, y: -5, turn: 0 }, categoryAliases: ["restricted_missing_stale", "restricted", "missing", "stale", "permission_limited"] }),
      staleRestrictedState: Object.freeze({ key: "stale_restricted_state", visualTokenKey: "staleRestricted", targetObjectKey: "stale_restricted_overlay" }),
    }),
    visualTokens: Object.freeze({
      brick: "brick",
      greenTrim: "green_trim",
      brassGoldHighlights: "brass_gold_highlights",
      warmLamps: "warm_lamps",
      woodDesks: "wood_desks",
      stoneFloor: "stone_floor",
      machinery: "machinery",
      paperLedger: "paper_ledger",
      blockedFailed: "blocked_failed",
      staleRestricted: "stale_restricted",
      completedStamp: "completed_stamp",
    }),
  });
  function office19thCenturyAnchor(anchorName) {
    return OFFICE_19TH_CENTURY_SCENE_CONFIG.semanticAnchors[anchorName] || {};
  }
  function parseOfficeGridTrack(value) {
    const match = String(value || "").match(/^\s*(\d+)(?:\s*\/\s*span\s*(\d+))?/i);
    return match ? { start: Number(match[1]), span: Number(match[2]) || 1 } : { start: 1, span: 1 };
  }
  const OFFICE_19TH_CENTURY_ZONE_LAYOUTS = Object.freeze((() => {
    const zones = OFFICE_19TH_CENTURY_SCENE_CONFIG.zones;
    return [
      Object.freeze({ zone: zones.receptionDispatch, employeeBucket: zones.receptionDispatch.key, title: "Front counter and dispatch board", column: "1 / span 2", row: "1 / span 1", scale: "front" }),
      Object.freeze({ zone: zones.mailTypewriterStation, employeeBucket: zones.mailTypewriterStation.key, title: "Letters, inbox, and typewriter", column: "1 / span 1", row: "2 / span 1", scale: "compact" }),
      Object.freeze({ zone: zones.archiveSourceRoom, employeeBucket: zones.archiveSourceRoom.key, title: "Tall shelves and source ledgers", column: "2 / span 1", row: "2 / span 2", scale: "tall" }),
      Object.freeze({ zone: zones.mainOfficeDesks, employeeBucket: zones.mainOfficeDesks.key, title: "Desks, lamps, and active ledgers", column: "3 / span 3", row: "1 / span 2", scale: "wide" }),
      Object.freeze({ zone: zones.projectRoomsCabinets, employeeBucket: zones.projectRoomsCabinets.key, title: "Project cabinets and context wall", column: "6 / span 3", row: "1 / span 2", scale: "wide" }),
      Object.freeze({ zone: zones.ticketPlanningTable, employeeBucket: zones.ticketPlanningTable.key, title: "Planning table and ticket packets", column: "3 / span 2", row: "3 / span 1", scale: "table" }),
      Object.freeze({ zone: zones.draftingTable, employeeBucket: zones.draftingTable.key, title: "Blueprint table and brass tools", column: "5 / span 2", row: "3 / span 1", scale: "table" }),
      Object.freeze({ zone: zones.handoffPathPoints, employeeBucket: zones.handoffPathPoints.key, title: "Crates, outbox, and delivery lane", column: "7 / span 2", row: "3 / span 1", scale: "path" }),
      Object.freeze({ zone: zones.commonArea, employeeBucket: zones.commonArea.key, title: "Tea bench and completed stamp tray", column: "1 / span 2", row: "4 / span 3", scale: "common" }),
      Object.freeze({ zone: zones.factoryMachines, employeeBucket: zones.factoryMachines.key, title: "Belts, pipes, and machine benches", column: "3 / span 6", row: "4 / span 3", scale: "factory" }),
    ];
  })());
  const OFFICE_ZONE_GRID_CENTERS = Object.freeze(OFFICE_19TH_CENTURY_ZONE_LAYOUTS.reduce((centers, layout) => {
    const column = parseOfficeGridTrack(layout.column);
    const row = parseOfficeGridTrack(layout.row);
    centers[layout.zone.key] = Object.freeze({
      x: column.start + (column.span / 2),
      y: row.start + (row.span / 2),
    });
    return centers;
  }, {}));
  function office19thCenturyPreset(anchorName, overrides) {
    const anchor = office19thCenturyAnchor(anchorName);
    return {
      key: anchor.presetKey || anchor.key,
      anchorId: anchor.key,
      label: anchor.label,
      sceneLabel: anchor.sceneLabel,
      icon: anchor.icon,
      tone: anchor.tone,
      zoneTone: anchor.zoneTone,
      zoneId: anchor.zoneKey,
      targetZone: anchor.targetZoneKey,
      targetObject: anchor.targetObjectKey,
      targetKey: anchor.targetObjectKey,
      targetProp: anchor.propGroupKey,
      objectClass: anchor.objectClass,
      motion: anchor.motion,
      categoryAliases: anchor.categoryAliases,
      ...overrides,
    };
  }
  function humanizeConfigKey(value) {
    return String(value || "").replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
  function officeZonePropGroups(zone) {
    const propGroups = OFFICE_19TH_CENTURY_SCENE_CONFIG.propGroups;
    return (Array.isArray(zone && zone.propGroupKeys) ? zone.propGroupKeys : [])
      .map((key) => propGroups[key])
      .filter(Boolean);
  }
  function officeZonePresetObjects(zone) {
    return (Array.isArray(zone && zone.anchorKeys) ? zone.anchorKeys : [])
      .map((anchorName) => office19thCenturyAnchor(anchorName))
      .map((anchor) => OFFICE_ANIMATION_PRESETS[anchor.presetKey || anchor.key])
      .filter((preset) => preset && preset.sceneLabel && !preset.nonDroppable);
  }
  function office19thCenturyPathPoints() {
    return (OFFICE_19TH_CENTURY_SCENE_CONFIG.zones.handoffPathPoints.pathPointKeys || [])
      .map((key) => ({ key, label: humanizeConfigKey(key) }));
  }
  function stateDisplay(employee) {
    const stateClass = employeeStateClass(employee);
    const labels = {
      running: ["RUNNING", "play"],
      queued: ["QUEUED", "clock"],
      delegated: ["DELEGATED", "handover"],
      blocked: ["BLOCKED", "stop"],
      failed: ["FAILED", "alert"],
      completed: ["RECENT", "check"],
      restricted: ["LOCKED", "shield"],
      missing: ["MISSING", "alert"],
      stale: ["STALE", "clock"],
      loading: ["LOADING", "clock"],
      idle: ["AVAILABLE", "clock"],
      fallback: ["GENERIC", "tool"],
    };
    return labels[stateClass] || labels.fallback;
  }
  const OFFICE_STATE_TREATMENTS = Object.freeze({
    idle: Object.freeze({ key: "idle", label: "Available", icon: "clock", prop: "availability_bench", tone: "idle" }),
    queued: Object.freeze({ key: "queued", label: "Dispatch queue", icon: "clock", prop: "dispatch_board", tone: "queued" }),
    running: Object.freeze({ key: "running", label: "Active station", icon: "play", prop: "active_station", tone: "running" }),
    delegated: Object.freeze({ key: "delegated", label: "Folder handoff", icon: "handover", prop: "folder_card_handoff", tone: "delegated" }),
    blocked: Object.freeze({ key: "blocked", label: "Jam stop", icon: "stop", prop: "jam_stop_badge", tone: "blocked" }),
    failed: Object.freeze({ key: "failed", label: "Broken machine", icon: "alert", prop: "broken_machine_error", tone: "failed" }),
    completed: Object.freeze({ key: "completed", label: "Stamped outbox", icon: "check", prop: "completed_stamp_tray", tone: "completed" }),
    restricted: Object.freeze({ key: "restricted", label: "Locked ledger", icon: "shield", prop: "locked_ledger", tone: "restricted" }),
    missing: Object.freeze({ key: "missing", label: "Question slip", icon: "alert", prop: "question_slip", tone: "missing" }),
    stale: Object.freeze({ key: "stale", label: "Clocked stale", icon: "clock", prop: "stale_clock", tone: "stale" }),
    loading: Object.freeze({ key: "loading", label: "Pending board", icon: "clock", prop: "loading_pending_board", tone: "loading" }),
    live_disconnected: Object.freeze({ key: "live-disconnected", label: "Disconnected clock", icon: "activity", prop: "stream_clock", tone: "stale" }),
    empty: Object.freeze({ key: "empty", label: "No employees", icon: "server", prop: "empty_shift_board", tone: "missing" }),
    permission_denied_after_load: Object.freeze({ key: "permission-denied-after-load", label: "Access lock", icon: "shield", prop: "access_lock", tone: "restricted" }),
  });
  function officeStateTreatment(key) {
    return OFFICE_STATE_TREATMENTS[key] || OFFICE_STATE_TREATMENTS.running;
  }
  function workTitle(item) {
    return (item && (item.title || item.displayLabel || item.currentWorkTitle)) || "Untitled work";
  }
  function workCategory(item) {
    const raw = item && (item.category || item.representativeCategory || (item.semanticEvent && item.semanticEvent.category));
    return employeeStateLabel(raw || "generic tool work");
  }
  function latestWorkTime(item) {
    return item && (item.updatedAt || item.lastEventAt || (item.timing && item.timing.updatedAt));
  }
  function workRunId(item) {
    return item && (item.runId || item.id);
  }
  function workThreadId(item) {
    const tool = (item && item.tool) || {};
    const refs = (item && item.animationIntent && item.animationIntent.backingReferences) || {};
    return item && (item.threadId || item.threadID) || tool.threadId || refs.threadId || "";
  }
  function workInstanceIdentity(item) {
    if (!item) return "";
    const semantic = item.semanticEvent || {};
    const refs = (item.animationIntent && item.animationIntent.backingReferences) || {};
    const employeeId = String(item.employeeId || semantic.employeeId || refs.employeeId || "");
    const candidates = [
      item.instanceId,
      item.workInstanceId,
      item.workItemId,
      item.runId,
      semantic.runId,
      refs.runId,
      item.id,
      workThreadId(item),
    ];
    const value = candidates.find((candidate) => hasValue(candidate) && String(candidate) !== employeeId);
    return value ? String(value) : "";
  }
  function workInstanceMissingKey(item, fallbackIndex) {
    return [
      "missing-instance",
      item && item.employeeId || "employee-missing",
      latestWorkTime(item) || "time-missing",
      workTitle(item).replace(/\s+/g, "_").slice(0, 44) || "untitled",
      fallbackIndex
    ].map((part) => String(part || "")).join(":");
  }
  function workInstanceSelectionId(item, fallbackIndex) {
    return workInstanceIdentity(item) || workInstanceMissingKey(item, fallbackIndex);
  }
  function workInstanceIdentityKind(item) {
    if (!item) return "";
    if (hasValue(item.instanceId) || hasValue(item.workInstanceId) || hasValue(item.workItemId)) return "instanceId";
    if (hasValue(item.runId) || hasValue(item.semanticEvent && item.semanticEvent.runId) || hasValue(item.animationIntent && item.animationIntent.backingReferences && item.animationIntent.backingReferences.runId) || hasValue(item.id)) return "runId";
    if (hasValue(workThreadId(item))) return "threadId";
    return "missing";
  }
  const OFFICE_SPEECH_SNIPPET_MAX_LENGTH = 96;
  const OFFICE_SPEECH_SOURCE_MAX_LENGTH = 220;
  function officeSpeechText(value) {
    return String(value || "").replace(/\s+/g, " ").trim();
  }
  function officeSpeechTextUnsafe(value) {
    const text = officeSpeechText(value);
    const clean = norm(text);
    if (!text) return true;
    if (text.length > OFFICE_SPEECH_SOURCE_MAX_LENGTH) return true;
    if (/https?:\/\//i.test(text) || /[?&](token|signature|sig|expires|se|sp|sv)=/i.test(text)) return true;
    if (/(authorization|credential|secret|password|api[_-]?key|bearer\s+)/i.test(text)) return true;
    if (/```|<\/?[a-z][\s\S]*?>|^\s*[{[]|["']\s*:\s*[{[]/.test(value || "")) return true;
    if (/(debug payload|tool payload|internal json|metadatajson|officechange|chain of thought)/i.test(text)) return true;
    if (/^#{1,6}\s/m.test(value || "") || /^\s*[-*+]\s+\S/m.test(value || "") || /\[[^\]]+\]\([^)]+\)/.test(value || "") || /`[^`]+`/.test(value || "")) return true;
    if (clean.includes("reasoning") && clean.includes("private")) return true;
    return false;
  }
  function officeSpeechSnippet(value) {
    const text = officeSpeechText(value);
    if (officeSpeechTextUnsafe(value)) return null;
    if (text.length <= OFFICE_SPEECH_SNIPPET_MAX_LENGTH) return { text, truncated: false };
    const cutAt = text.lastIndexOf(" ", OFFICE_SPEECH_SNIPPET_MAX_LENGTH - 4);
    return { text: `${text.slice(0, cutAt > 40 ? cutAt : OFFICE_SPEECH_SNIPPET_MAX_LENGTH - 3).trim()}...`, truncated: true };
  }
  function officeLatestMessageIsSpeech(event) {
    const kind = norm(event && event.latestMessageKind);
    const role = norm(event && event.latestMessageRole);
    if (role && !["assistant", "employee", "ai", "system-assistant"].includes(role)) return false;
    if (kind.includes("tool") || kind.includes("debug") || kind.includes("internal") || kind.includes("payload") || kind.includes("log")) return false;
    return true;
  }
  function officeSpeechBubbleForWork(item) {
    if (!item) return null;
    const semantic = item.semanticEvent || {};
    const queue = item.queue || {};
    const event = item.event || {};
    const delegation = item.delegation || {};
    const status = norm(item.status || item.state || semantic.status);
    const candidates = [];
    if (officeLatestMessageIsSpeech(event)) candidates.push({ source: "latest-message", value: event.latestMessageText });
    if (queue.needsInput || queue.approvalRequestTitle) candidates.push({ source: "approval-prompt", value: queue.approvalRequestTitle });
    if (delegation.handoffLabel) candidates.push({ source: "handoff-summary", value: delegation.handoffLabel });
    if (status.includes("block") || status.includes("need") || status.includes("fail") || status.includes("complete")) candidates.push({ source: "visible-status", value: item.detail });
    for (const candidate of candidates) {
      const snippet = officeSpeechSnippet(candidate.value);
      if (snippet) return { ...snippet, source: candidate.source };
    }
    return null;
  }
  function fieldStateTone(state) {
    const clean = norm(state);
    if (clean === "available") return "available";
    if (clean === "restricted" || clean.includes("denied") || clean.includes("permission")) return "restricted";
    if (clean === "loading") return "loading";
    if (clean === "stale") return "stale";
    return "missing";
  }
  function fieldLabel(value) {
    const text = String(value || "field").replace(/[._-]+/g, " ");
    return text.split(/\s+/).filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
  }
  function selectedOfficeWork(snapshot, employee) {
    const work = Array.isArray(snapshot && snapshot.work) ? snapshot.work : [];
    const currentRunId = employee && ((employee.currentWork && employee.currentWork.runId) || employee.currentRunId);
    if (!currentRunId) return null;
    return work.find((item) => String(item && item.runId) === String(currentRunId)) || null;
  }
  function inspectionFieldStates(employee, work) {
    const employeeFields = Array.isArray(employee && employee.fieldStates) ? employee.fieldStates : [];
    const workFields = Array.isArray(work && work.fieldStates) ? work.fieldStates : [];
    return [...employeeFields, ...workFields].filter((field) => field && norm(field.state) !== "available");
  }
  function debugPayload(work, currentWork) {
    if (work) return work.debug || work;
    return currentWork || null;
  }
  function employeeById(employees, id) {
    return employees.find((employee) => String(employee && employee.id) === String(id)) || null;
  }
  function workByRunId(snapshot, runId) {
    const work = Array.isArray(snapshot && snapshot.work) ? snapshot.work : [];
    return work.find((item) => String(workRunId(item)) === String(runId)) || null;
  }
  function workIntentId(item) {
    return animationIntentId(item && item.animationIntent);
  }
  function workBySelectionId(snapshot, id, employees) {
    const selectionId = String(id || "");
    const work = Array.isArray(snapshot && snapshot.work) ? snapshot.work : [];
    const fromWork = work.find((item, index) => {
      const values = [
        workRunId(item),
        workIntentId(item),
        workThreadId(item),
        workInstanceIdentity(item),
        workInstanceSelectionId(item, index),
      ];
      return values.some((value) => hasValue(value) && String(value) === selectionId);
    });
    if (fromWork) return fromWork;
    const refs = [];
    (Array.isArray(employees) ? employees : []).forEach((employee) => {
      if (employee && employee.currentWork) refs.push({ ...employee.currentWork, employeeId: employee.id });
      (Array.isArray(employee && employee.recentHistory) ? employee.recentHistory : []).forEach((item) => {
        refs.push({ ...item, employeeId: employee.id });
      });
    });
    return refs.find((item, index) => {
      const values = [workRunId(item), workIntentId(item), workThreadId(item), workInstanceIdentity(item), workInstanceSelectionId(item, index)];
      return values.some((value) => hasValue(value) && String(value) === selectionId);
    }) || null;
  }
  function workItemsByPresetKey(snapshot, presetKey) {
    const work = Array.isArray(snapshot && snapshot.work) ? snapshot.work : [];
    return work
      .filter((item) => officePresetForWork(item).key === presetKey)
      .sort((a, b) => new Date(latestWorkTime(b) || 0) - new Date(latestWorkTime(a) || 0));
  }
  function workByPresetKey(snapshot, presetKey) {
    return workItemsByPresetKey(snapshot, presetKey)[0] || null;
  }
  function runIdFromReference(reference) {
    if (!reference) return "";
    if (typeof reference === "string") return reference;
    return reference.runId || reference.id || "";
  }
  function compressedGroupWorkItems(snapshot, group) {
    const work = Array.isArray(snapshot && snapshot.work) ? snapshot.work : [];
    if (!group || !work.length) return [];
    const referencedRunIds = [
      ...(Array.isArray(group.items) ? group.items.map(runIdFromReference) : []),
      ...(Array.isArray(group.itemIds) ? group.itemIds.map(runIdFromReference) : []),
      ...(Array.isArray(group.runIds) ? group.runIds.map(runIdFromReference) : []),
    ].filter(hasValue).map((runId) => String(runId));
    const referenced = new Set(referencedRunIds);
    const compressionKey = group.compressionKey;
    return work
      .filter((item) => {
        const runId = String(workRunId(item) || "");
        if (referenced.has(runId)) return true;
        const compression = item && item.compression;
        return compressionKey && compression && String(compression.compressionKey) === String(compressionKey);
      })
      .sort((a, b) => new Date(latestWorkTime(b) || 0) - new Date(latestWorkTime(a) || 0));
  }
  const OFFICE_COMPRESSED_BUNDLE_STATUS_PRIORITY = Object.freeze(["failed", "blocked", "restricted", "stale", "missing", "loading", "delegated", "queued", "running", "completed"]);
  function officeStatusPriority(statusKey) {
    const index = OFFICE_COMPRESSED_BUNDLE_STATUS_PRIORITY.indexOf(statusKey);
    return index < 0 ? OFFICE_COMPRESSED_BUNDLE_STATUS_PRIORITY.length : index;
  }
  function officeHighestStatusKey(statusKeys) {
    const keys = (Array.isArray(statusKeys) ? statusKeys : []).filter(Boolean);
    return keys.sort((a, b) => officeStatusPriority(a) - officeStatusPriority(b))[0] || "running";
  }
  function officeStatusKeysFromSummary(value, depth) {
    if (!value || depth > 2) return [];
    if (typeof value === "string") return [officeStatusKeyForParts(value, null, null)];
    if (Array.isArray(value)) return value.flatMap((item) => officeStatusKeysFromSummary(item, depth + 1));
    if (typeof value !== "object") return [];
    const keys = [];
    Object.entries(value).forEach(([key, entry]) => {
      if (typeof entry === "number" && entry > 0) keys.push(officeStatusKeyForParts(key, null, null));
      if (["status", "state", "key", "label", "representativeStatus"].includes(key) && typeof entry === "string") keys.push(officeStatusKeyForParts(entry, null, null));
      if (typeof entry === "object") keys.push(...officeStatusKeysFromSummary(entry, depth + 1));
    });
    return keys;
  }
  function officeCompressedWorkStatusKeys(item) {
    const semantic = (item && item.semanticEvent) || {};
    const intent = (item && item.animationIntent) || {};
    const category = semantic.category || item && item.category || intent.category || "";
    const status = semantic.status || item && (item.status || item.state) || intent.status || "";
    const keys = [
      officeStatusKeyForParts(status || officeRestrictedStatusFromCategory(category), item && item.fieldStates, item && item.queue),
    ];
    if (isDelegationWork(item)) keys.push("delegated");
    return keys;
  }
  function officeCompressedBundleCategory(group, workItems) {
    const fromGroup = group && (group.representativeCategory || group.category || group.semanticCategory);
    if (hasValue(fromGroup)) return fromGroup;
    const source = (Array.isArray(workItems) ? workItems : []).find((item) => hasValue((item && item.category) || (item && item.semanticEvent && item.semanticEvent.category) || (item && item.animationIntent && item.animationIntent.category)));
    return (source && ((source.semanticEvent && source.semanticEvent.category) || source.category || (source.animationIntent && source.animationIntent.category))) || "generic_tool_work";
  }
  function officeCompressedBundleVisual(category, presetKey) {
    const key = presetKey || officePresetKeyForParts(category, "", "");
    if (key === "source_review" || key === "project_context_read") return { key: "paper-stack", label: "Archive stack", icon: "files" };
    if (key === "ticket_work_package") return { key: "ledger-cards", label: "Ledger cards", icon: "list" };
    if (key === "document_note_email") return { key: "archive-folders", label: "Folder bundle", icon: "folder" };
    if (key === "delegation") return { key: "handoff-folder", label: "Handoff folder", icon: "handover" };
    if (key === "tool_call" || key === "command_execution" || key === "test_check") return { key: "machine-counter", label: "Machine batch", icon: "gauge" };
    if (key === "restricted_missing_stale") return { key: "limited-ledger", label: "Limited ledger", icon: "shield" };
    return { key: "neutral-workbench", label: "Workbench batch", icon: "tool" };
  }
  function officeCompressedBundleSummary(group, workItems) {
    const items = Array.isArray(workItems) ? workItems : [];
    const category = officeCompressedBundleCategory(group, items);
    const groupStatus = group && (group.representativeStatus || group.status || group.state);
    const statusKeys = [
      officeStatusKeyForParts(groupStatus || officeRestrictedStatusFromCategory(category), group && group.fieldStates, group && group.queue),
      ...officeStatusKeysFromSummary(group && (group.statusSummary || group.statusSummaries || group.statusCounts || group.summaryStatuses), 0),
      ...items.flatMap(officeCompressedWorkStatusKeys),
    ].filter(Boolean);
    if (norm(category).includes("delegat")) statusKeys.push("delegated");
    const uniquePromoted = Array.from(new Set(statusKeys))
      .filter((key) => ["failed", "blocked", "delegated", "restricted", "stale", "missing", "loading"].includes(key))
      .sort((a, b) => officeStatusPriority(a) - officeStatusPriority(b));
    const statusKey = officeHighestStatusKey(statusKeys);
    const presetKey = officePresetKeyForParts(category, "", statusKey);
    const visual = officeCompressedBundleVisual(category, presetKey);
    return {
      category,
      statusKey,
      statusLabel: employeeStateLabel(statusKey),
      promotedStatusKeys: uniquePromoted,
      visual,
      presetKey,
      resolvedItemCount: items.length,
    };
  }
  function workEmployee(employees, work) {
    return work ? employeeById(employees, work.employeeId) : null;
  }
  function isDelegationWork(item) {
    const semantic = (item && item.semanticEvent) || {};
    const intent = (item && item.animationIntent) || {};
    const category = norm(semantic.category || item && item.category || intent.category);
    const sequence = norm(intent.sequenceKey);
    return category === "delegation" || sequence === "delegation" || category.includes("delegat") || sequence.includes("delegat");
  }
  function officeDelegationFieldState(value, resolved, loadingWhenMissing) {
    if (!hasValue(value)) return loadingWhenMissing ? "loading" : "missing";
    return resolved ? "available" : "missing";
  }
  function officeDelegationInfo(work, employees) {
    const semantic = (work && work.semanticEvent) || {};
    const intent = (work && work.animationIntent) || {};
    const delegation = (work && work.delegation) || {};
    const sourceEmployeeId = intent.sourceEmployeeId || semantic.parentEmployeeId || delegation.parentEmployeeId || work && work.employeeId || "";
    const targetEmployeeId = intent.targetEmployeeId || semantic.childEmployeeId || "";
    const sourceEmployee = employeeById(employees, sourceEmployeeId);
    const targetEmployee = employeeById(employees, targetEmployeeId);
    const parentRunId = intent.parentRunId || delegation.parentRunId || semantic.parentRunId || "";
    const childRunId = intent.childRunId || semantic.childRunId || "";
    const sourceState = officeDelegationFieldState(sourceEmployeeId, sourceEmployee, false);
    const targetState = officeDelegationFieldState(targetEmployeeId, targetEmployee, false);
    const childRunState = childRunId ? "available" : "loading";
    const targetActiveState = targetEmployee ? employeeStateClass(targetEmployee) : "";
    const nextZone = targetEmployee ? employeeZone(targetEmployee) : norm(intent.targetZone || "handoff");
    return {
      sourceEmployeeId,
      targetEmployeeId,
      sourceEmployee,
      targetEmployee,
      parentRunId,
      childRunId,
      sourceState,
      targetState,
      parentRunState: parentRunId ? "available" : "missing",
      childRunState,
      targetActiveState,
      sameEmployee: hasValue(sourceEmployeeId) && hasValue(targetEmployeeId) && String(sourceEmployeeId) === String(targetEmployeeId),
      handoffLabel: delegation.handoffLabel || semantic.displayLabel || workTitle(work),
      nextZone: nextZone || "handoff",
    };
  }
  function officeDelegationStageState(stageKey, work, info) {
    const status = norm(work && (work.status || work.state || (work.semanticEvent && work.semanticEvent.status)));
    const workState = workStateClass(work);
    const completed = workState === "done" || status.includes("complete") || status.includes("done");
    const targetMissing = info.targetState !== "available";
    const childMissing = info.childRunState !== "available";
    const targetBlocked = info.targetActiveState === "blocked";
    const targetFailed = info.targetActiveState === "failed";
    if (stageKey === "source_state") return info.sourceState === "available" ? "complete" : "missing";
    if (stageKey === "dispatch_point") return "current";
    if (stageKey === "target_acknowledges") {
      if (targetMissing) return "missing";
      if (targetBlocked) return "blocked";
      if (targetFailed) return "failed";
      return "complete";
    }
    if (stageKey === "folder_card_handoff") {
      if (targetBlocked) return "blocked";
      if (targetFailed) return "failed";
      if (childMissing) return "waiting";
      return "complete";
    }
    if (stageKey === "target_station") {
      if (targetMissing || childMissing) return "waiting";
      if (targetBlocked) return "blocked";
      if (targetFailed) return "failed";
      return completed ? "complete" : "current";
    }
    if (stageKey === "handback") return completed ? "complete" : "waiting";
    return "waiting";
  }
  function officeDelegationChoreographyStages(work, info) {
    return [
      { key: "source_state", label: "Source state", detail: info.sourceEmployee ? info.sourceEmployee.name : "Source missing", prop: "dispatch board" },
      { key: "dispatch_point", label: "Dispatch point", detail: "Moves to handoff lane", prop: "dispatch board" },
      { key: "target_acknowledges", label: "Target acknowledges", detail: info.targetEmployee ? info.targetEmployee.name : (info.targetEmployeeId || "Target missing"), prop: "call slip" },
      { key: "folder_card_handoff", label: "Folder handoff", detail: info.childRunId ? "Child run linked" : "Child run pending", prop: "folder card" },
      { key: "target_station", label: "Next station", detail: info.nextZone || "handoff", prop: "work station" },
      { key: "handback", label: "Hand-back", detail: workStateClass(work) === "done" ? "Stamped to outbox" : "Outbox pending", prop: "outbox stamp board" },
    ].map((stage) => ({ ...stage, state: officeDelegationStageState(stage.key, work, info) }));
  }
  function officeDelegationHandoffs(snapshot, employees) {
    const work = Array.isArray(snapshot && snapshot.work) ? snapshot.work : [];
    return work
      .filter(isDelegationWork)
      .map((item) => ({ work: item, info: officeDelegationInfo(item, employees) }))
      .sort((a, b) => new Date(latestWorkTime(b.work) || 0) - new Date(latestWorkTime(a.work) || 0))
      .slice(0, MAX_OFFICE_VISIBLE_DELEGATION_HANDOFFS);
  }
  function officeDelegationRole(employeeId, handoffs) {
    const id = String(employeeId || "");
    if (!id) return "";
    const roles = new Set();
    (Array.isArray(handoffs) ? handoffs : []).forEach((handoff) => {
      const info = handoff && handoff.info;
      if (!info) return;
      if (String(info.sourceEmployeeId || "") === id) roles.add("source");
      if (String(info.targetEmployeeId || "") === id) roles.add("target");
    });
    if (roles.has("source") && roles.has("target")) return "same";
    if (roles.has("source")) return "source";
    if (roles.has("target")) return "target";
    return "";
  }
  function compareByNameThenId(a, b) {
    const left = String((a && a.name) || "").localeCompare(String((b && b.name) || ""), undefined, { sensitivity: "base" });
    if (left !== 0) return left;
    return String((a && a.id) || "").localeCompare(String((b && b.id) || ""));
  }
  function orderedEmployees(snapshot) {
    const employees = Array.isArray(snapshot && snapshot.employees) ? snapshot.employees.slice() : [];
    return employees
      .map((employee, index) => ({ ...employee, __officeOrder: Number.isFinite(employee && employee.displayOrder) ? employee.displayOrder : index }))
      .sort((a, b) => {
        const order = (Number.isFinite(a.__officeOrder) ? a.__officeOrder : 0) - (Number.isFinite(b.__officeOrder) ? b.__officeOrder : 0);
        return order || compareByNameThenId(a, b);
      });
  }
  function officeZoneKeyForPresetTone(preset) {
    if (!preset) return "factory_machines";
    if (preset.zoneTone === "dispatch") return "reception_dispatch";
    if (preset.zoneTone === "project") return "project_rooms_cabinets";
    if (preset.zoneTone === "common") return "common_area";
    if (preset.zoneTone === "floor") return "factory_machines";
    return preset.zoneId || "factory_machines";
  }
  function activeWorkZoneKey(employee, currentWork, preset) {
    if (!currentWork) return "";
    const movementTarget = resolveOfficeSemanticMovementTarget(currentWork, {
      employeeId: employee && employee.id,
      selectionId: workInstanceIdentity(currentWork) || workRunId(currentWork) || employee && employee.id,
    });
    return norm(movementTarget && movementTarget.zoneId) || officeZoneKeyForPresetTone(preset);
  }
  function employeeZone(employee, sequencer) {
    const state = employeeStateClass(employee);
    const sequencedState = sequencer && sequencer.employees && sequencer.employees[String(employee && employee.id)];
    const sequencedCurrent = sequencedState && sequencedState.current;
    if (sequencedCurrent && state !== "idle" && state !== "completed") {
      return norm(sequencedCurrent.movementZoneId) || officeZoneKeyForPresetTone(OFFICE_ANIMATION_PRESETS[sequencedCurrent.presetKey]);
    }
    const currentWork = employee && employee.currentWork;
    const semantic = (currentWork && currentWork.semanticEvent) || {};
    const intent = (currentWork && currentWork.animationIntent) || {};
    const placementPresetKey = officePresetKeyForParts(semantic.category || currentWork && currentWork.category || intent.category, intent.sequenceKey, "");
    const preset = OFFICE_ANIMATION_PRESETS[placementPresetKey] || OFFICE_ANIMATION_PRESETS.generic_tool_work;
    if (state === "idle" || state === "completed") return "common_area";
    if (state === "queued") return "reception_dispatch";
    return activeWorkZoneKey(employee, currentWork, preset) || officeZoneKeyForPresetTone(preset);
  }
  function zoneEmployees(employees, zone, sequencer) {
    return employees.filter((employee) => employeeZone(employee, sequencer) === zone);
  }
  function compressedMarkers(snapshot, employees) {
    const markers = [];
    employees.forEach((employee) => {
      (Array.isArray(employee.compressedGroups) ? employee.compressedGroups : []).forEach((group, index) => {
        if (!group || Number(group.compressedCount) <= 1) return;
        const workItems = compressedGroupWorkItems(snapshot, group);
        const bundle = officeCompressedBundleSummary(group, workItems);
        markers.push({
          id: `${employee.id || "employee"}-${group.compressionKey || index}`,
          group,
          employee,
          count: Number(group.compressedCount) || 0,
          category: bundle.category,
          status: bundle.statusKey,
          bundle,
          updatedAt: group.lastEventAt,
        });
      });
    });
    const work = Array.isArray(snapshot && snapshot.work) ? snapshot.work : [];
    work.forEach((item) => {
      const compression = item && item.compression;
      if (!compression || Number(compression.compressedCount) <= 1) return;
      const workItems = compressedGroupWorkItems(snapshot, compression);
      const bundle = officeCompressedBundleSummary(compression, workItems.length ? workItems : [item]);
      markers.push({
        id: `${item.runId || "work"}-${compression.compressionKey || "compressed"}`,
        group: compression,
        employee: employees.find((employee) => String(employee.id) === String(item.employeeId)),
        count: Number(compression.compressedCount) || 0,
        category: bundle.category,
        status: bundle.statusKey,
        bundle,
        updatedAt: compression.lastEventAt || (item.timing && item.timing.updatedAt),
      });
    });
    const seen = new Set();
    return markers
      .filter((marker) => {
        const key = marker.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
      .slice(0, MAX_OFFICE_VISIBLE_COMPRESSED_MARKERS);
  }
  function defaultSelection(employees) {
    const employee = employees.find((item) => employeeStateClass(item) !== "idle") || employees[0];
    return employee ? { kind: "employee", id: employee.id } : null;
  }
  function selectionKey(selection) {
    return selection ? `${selection.kind}:${selection.id}` : "";
  }
  function compressedSelectionId(employeeId, group, fallbackIndex) {
    return `${employeeId || "employee"}:${group && (group.compressionKey || group.firstEventAt || group.lastEventAt || fallbackIndex)}`;
  }
  function employeeActiveWorkInstances(snapshot, employee) {
    if (!employee) return [];
    const employeeId = String(employee.id || "");
    const items = [];
    const seen = new Set();
    const add = (item, source) => {
      if (!item || !IsActiveWork(item)) return;
      const withEmployee = item.employeeId ? item : { ...item, employeeId };
      if (String(withEmployee.employeeId || "") !== employeeId) return;
      const key = workInstanceSelectionId(withEmployee, items.length);
      if (seen.has(key)) return;
      seen.add(key);
      items.push({ item: withEmployee, source, selectionId: key, missingIdentity: !workInstanceIdentity(withEmployee) });
    };
    (Array.isArray(snapshot && snapshot.work) ? snapshot.work : []).forEach((item) => add(item, "snapshot"));
    add(employee.currentWork, "current");
    return items.sort((a, b) => new Date(latestWorkTime(b.item) || 0) - new Date(latestWorkTime(a.item) || 0));
  }
  function workSelection(item, fallbackIndex, employeeId) {
    const withEmployee = item && item.employeeId ? item : { ...(item || {}), employeeId };
    return { kind: "work", id: workInstanceSelectionId(withEmployee, fallbackIndex || 0) };
  }
  function employeePrimarySelection(employee, instances) {
    const activeInstances = Array.isArray(instances) ? instances : [];
    if (activeInstances.length === 1) return { kind: "work", id: activeInstances[0].selectionId };
    const currentWork = employee && employee.currentWork;
    if (currentWork && IsActiveWork(currentWork)) return workSelection(currentWork, 0, employee && employee.id);
    return { kind: "employee", id: employee && employee.id };
  }
  function compressedSelectionForWorkItems(snapshot, employees, workItems, presetKey) {
    const runIds = new Set((Array.isArray(workItems) ? workItems : []).map(workRunId).filter(hasValue).map((runId) => String(runId)));
    if (!runIds.size) return null;
    return officeRailItems(snapshot, employees).find((item) => {
      if (item.kind !== "compressed") return false;
      const items = compressedGroupWorkItems(snapshot, item.group);
      const matchesRun = items.some((work) => runIds.has(String(workRunId(work) || "")));
      const matchesPreset = !presetKey || officePresetKeyForParts(item.category, "", item.status) === presetKey || items.some((work) => officePresetForWork(work).key === presetKey);
      return matchesRun && matchesPreset;
    }) || null;
  }
  function selectionForPresetObject(snapshot, employees, preset) {
    const workItems = workItemsByPresetKey(snapshot, preset && preset.key);
    if (workItems.length === 1) return workSelection(workItems[0], 0, workItems[0] && workItems[0].employeeId);
    if (workItems.length > 1) {
      const compressed = compressedSelectionForWorkItems(snapshot, employees, workItems, preset && preset.key);
      if (compressed) return { kind: "compressed", id: compressed.id };
    }
    return { kind: "zone", id: preset && preset.key };
  }
  function presetsForPropGroup(propGroup) {
    return Object.values(OFFICE_ANIMATION_PRESETS)
      .filter((preset) => preset && preset.targetProp === propGroup.key && preset.sceneLabel && !preset.nonDroppable);
  }
  function selectionForPropGroup(snapshot, employees, propGroup) {
    const presets = presetsForPropGroup(propGroup);
    const workItems = presets.flatMap((preset) => workItemsByPresetKey(snapshot, preset.key));
    if (workItems.length === 1) return workSelection(workItems[0], 0, workItems[0] && workItems[0].employeeId);
    if (workItems.length > 1) {
      const compressed = compressedSelectionForWorkItems(snapshot, employees, workItems, presets[0] && presets[0].key);
      if (compressed) return { kind: "compressed", id: compressed.id };
    }
    if (presets[0]) return { kind: "zone", id: presets[0].key };
    return {
      kind: "zone",
      id: `prop:${propGroup.key}`,
      sceneObject: {
        key: `prop:${propGroup.key}`,
        sceneLabel: humanizeConfigKey(propGroup.key),
        targetZone: propGroup.zoneKey,
        targetObject: propGroup.key,
        targetProp: propGroup.key,
      },
    };
  }
  const OFFICE_ANIMATION_PRESETS = {
    action_center_activity: office19thCenturyPreset("actionCenterActivity"),
    project_context_read: office19thCenturyPreset("projectContextRead"),
    source_review: office19thCenturyPreset("sourceReview"),
    ticket_work_package: office19thCenturyPreset("ticketWorkPackage"),
    document_note_email: office19thCenturyPreset("documentNoteEmail"),
    tool_call: office19thCenturyPreset("toolCall"),
    command_execution: office19thCenturyPreset("commandExecution"),
    test_check: office19thCenturyPreset("testCheck"),
    planning_thinking: office19thCenturyPreset("planningThinking"),
    generic_tool_work: office19thCenturyPreset("genericToolWork"),
    delegation: office19thCenturyPreset("delegation"),
    blocked: office19thCenturyPreset("blocked", { nonDroppable: true }),
    failed: office19thCenturyPreset("failed", { nonDroppable: true }),
    completed: office19thCenturyPreset("completed"),
    restricted_missing_stale: office19thCenturyPreset("restrictedMissingStale"),
    idle_available: office19thCenturyPreset("idleAvailable", { ambientOnly: true }),
  };
  const OFFICE_ANIMATION_PRESET_ALIAS = Object.values(OFFICE_ANIMATION_PRESETS).reduce((aliases, preset) => {
    aliases[preset.key] = preset.key;
    (preset.categoryAliases || []).forEach((alias) => { aliases[norm(alias)] = preset.key; });
    return aliases;
  }, {});
  const OFFICE_ACTIVITY_STATUS_VARIANTS = Object.freeze({
    queued: Object.freeze({ key: "queued", label: "QUEUED", icon: "clock", tone: "queued" }),
    running: Object.freeze({ key: "running", label: "RUNNING", icon: "play", tone: "running" }),
    completed: Object.freeze({ key: "completed", label: "DONE", icon: "check", tone: "completed" }),
    delegated: Object.freeze({ key: "delegated", label: "HANDOFF", icon: "handover", tone: "delegated" }),
    blocked: Object.freeze({ key: "blocked", label: "BLOCKED", icon: "stop", tone: "blocked" }),
    failed: Object.freeze({ key: "failed", label: "FAILED", icon: "alert", tone: "failed" }),
    stale: Object.freeze({ key: "stale", label: "STALE", icon: "clock", tone: "stale" }),
    restricted: Object.freeze({ key: "restricted", label: "LOCKED", icon: "shield", tone: "restricted" }),
    missing: Object.freeze({ key: "missing", label: "MISSING", icon: "alert", tone: "missing" }),
    loading: Object.freeze({ key: "loading", label: "LOADING", icon: "clock", tone: "loading" }),
  });
  function officeFieldStateStatus(fieldStates) {
    const states = (Array.isArray(fieldStates) ? fieldStates : [])
      .map((field) => norm(field && field.state))
      .filter(Boolean);
    if (states.some((state) => state.includes("restricted") || state.includes("denied") || state.includes("permission"))) return "restricted";
    if (states.some((state) => state.includes("stale"))) return "stale";
    if (states.some((state) => state.includes("missing") || state.includes("unavailable"))) return "missing";
    if (states.some((state) => state.includes("loading") || state.includes("pending"))) return "loading";
    return "";
  }
  function officeStatusKeyForParts(status, fieldStates, queue) {
    const fieldStateStatus = officeFieldStateStatus(fieldStates);
    const cleanStatus = norm(status);
    if (cleanStatus.includes("restricted") || cleanStatus.includes("denied") || cleanStatus.includes("forbidden") || cleanStatus.includes("permission")) return "restricted";
    if (cleanStatus.includes("stale")) return "stale";
    if (cleanStatus.includes("missing") || cleanStatus.includes("unavailable") || cleanStatus.includes("not_found")) return "missing";
    if (cleanStatus.includes("loading") || cleanStatus.includes("pending")) return "loading";
    if (fieldStateStatus) return fieldStateStatus;
    if (cleanStatus.includes("fail") || cleanStatus.includes("error")) return "failed";
    if (cleanStatus.includes("block") || cleanStatus.includes("need")) return "blocked";
    if (cleanStatus.includes("delegat") || cleanStatus.includes("handoff")) return "delegated";
    if (queue && (queue.queued || queue.waitingForDelegates)) return "queued";
    if (cleanStatus.includes("queue") || cleanStatus.includes("wait")) return "queued";
    if (cleanStatus.includes("complete") || cleanStatus.includes("done")) return "completed";
    return "running";
  }
  function officeRestrictedStatusFromCategory(category) {
    const cleanCategory = norm(category);
    if (cleanCategory.includes("restricted") || cleanCategory.includes("denied") || cleanCategory.includes("permission")) return "restricted";
    if (cleanCategory.includes("stale")) return "stale";
    if (cleanCategory.includes("missing")) return "missing";
    return "";
  }
  function officeSemanticMovementParts(item, fallback) {
    const semantic = (item && item.semanticEvent) || {};
    const intent = (item && item.animationIntent) || item || {};
    const refs = (intent && intent.backingReferences) || {};
    const queue = (item && item.queue) || (intent && intent.queue) || {};
    const intentOnly = item && !item.animationIntent && (item.backingReferences || item.intentId || item.sequenceKey);
    const category = semantic.category || (item && item.category) || refs.semanticCategory || (intent && intent.category) || "";
    const status = semantic.status || (item && (item.status || item.state)) || refs.semanticStatus || (intent && intent.status) || "";
    const sequenceKey = (intent && intent.sequenceKey) || "";
    const employeeId = (item && item.employeeId) || semantic.employeeId || refs.employeeId || (intent && intent.sourceEmployeeId) || (fallback && fallback.employeeId) || "";
    return {
      category,
      status,
      sequenceKey,
      queue,
      fieldStates: item && item.fieldStates,
      employeeId,
      instanceId: workInstanceIdentity(item) || animationIntentId(intent) || (fallback && fallback.selectionId) || "",
      runId: intentOnly ? (semantic.runId || refs.runId || animationIntentRunId(intent)) : workRunId(item) || semantic.runId || refs.runId || animationIntentRunId(intent),
      threadId: workThreadId(item) || refs.threadId || "",
    };
  }
  function officeMovementPresetKeyForParts(category, sequenceKey, status, fieldStates, queue) {
    const cleanCategory = norm(category);
    const cleanSequence = norm(sequenceKey);
    const cleanStatus = norm(status || officeRestrictedStatusFromCategory(category));
    const statusKey = officeStatusKeyForParts(cleanStatus, fieldStates, queue);
    if (["restricted", "missing", "stale", "loading"].includes(statusKey)) return "restricted_missing_stale";
    if (statusKey === "failed") return "failed";
    if (statusKey === "blocked") return "blocked";
    if (statusKey === "completed") return "completed";
    if (cleanSequence.includes("idle")) return "idle_available";
    if (cleanCategory && OFFICE_ANIMATION_PRESET_ALIAS[cleanCategory]) return OFFICE_ANIMATION_PRESET_ALIAS[cleanCategory];
    if (cleanSequence && OFFICE_ANIMATION_PRESET_ALIAS[cleanSequence]) return OFFICE_ANIMATION_PRESET_ALIAS[cleanSequence];
    if (cleanCategory.includes("action_center") || cleanCategory.includes("dispatch") || cleanCategory.includes("intake") || cleanCategory.includes("approval") || cleanCategory.includes("question") || queue.approvalRequestId || queue.approvalRequestTitle || queue.needsInput) return "action_center_activity";
    if (cleanCategory.includes("delegat") || cleanSequence.includes("delegat")) return "delegation";
    if (cleanCategory.includes("sketch") || cleanCategory.includes("design") || cleanCategory.includes("drafting") || cleanCategory.includes("blueprint")) return "planning_thinking";
    if (cleanCategory.includes("source") || cleanCategory.includes("archive") || cleanCategory.includes("attachment")) return "source_review";
    if (cleanCategory.includes("note") || cleanCategory.includes("document") || cleanCategory.includes("email") || cleanCategory.includes("mail")) return "document_note_email";
    if (cleanCategory.includes("work_package") || cleanCategory.includes("ticket")) return "ticket_work_package";
    if (cleanCategory.includes("project") || cleanCategory.includes("context") || cleanCategory.includes("knowledge") || cleanCategory.includes("okf")) return "project_context_read";
    if (cleanCategory.includes("command") || cleanCategory.includes("terminal") || cleanCategory.includes("shell") || cleanCategory.includes("exec")) return "command_execution";
    if (cleanCategory.includes("test") || cleanCategory.includes("verification") || cleanCategory.includes("check")) return "test_check";
    if (cleanCategory.includes("plan") || cleanCategory.includes("thinking") || cleanCategory.includes("checking_files")) return "planning_thinking";
    if (cleanCategory.includes("tool")) return "tool_call";
    return "generic_tool_work";
  }
  function officeMovementAnchorNameForParts(category, presetKey, sequenceKey) {
    const cleanCategory = norm(category);
    const cleanSequence = norm(sequenceKey);
    if (cleanCategory.includes("sketch") || cleanCategory.includes("design") || cleanCategory.includes("drafting") || cleanCategory.includes("blueprint")) return "draftingTable";
    if ((cleanCategory.includes("email") || cleanCategory.includes("mail") || cleanCategory.includes("document")) && (cleanCategory.includes("draft") || cleanCategory.includes("compose") || cleanCategory.includes("write") || cleanSequence.includes("draft") || cleanSequence.includes("compose") || cleanSequence.includes("write"))) return "mailTypewriter";
    const anchorNames = {
      action_center_activity: "actionCenterActivity",
      project_context_read: "projectContextRead",
      source_review: "sourceReview",
      ticket_work_package: "ticketWorkPackage",
      document_note_email: "documentNoteEmail",
      tool_call: "toolCall",
      command_execution: "commandExecution",
      test_check: "testCheck",
      planning_thinking: "planningThinking",
      generic_tool_work: "genericToolWork",
      delegation: "delegation",
      blocked: "blocked",
      failed: "failed",
      completed: "completed",
      restricted_missing_stale: "restrictedMissingStale",
      idle_available: "idleAvailable",
    };
    return anchorNames[presetKey] || "genericToolWork";
  }
  function officeMovementFallbackReason(parts, presetKey) {
    const reasons = [];
    if (!hasValue(parts.category)) reasons.push("missing_semantic_category");
    if (!hasValue(parts.status)) reasons.push("missing_status");
    if (!hasValue(parts.employeeId)) reasons.push("missing_employee_id");
    if (!hasValue(parts.instanceId)) reasons.push("missing_work_instance_id");
    if (presetKey === "generic_tool_work") reasons.push("unknown_semantic_category");
    if (presetKey === "restricted_missing_stale") reasons.push(officeStatusKeyForParts(parts.status, parts.fieldStates, parts.queue));
    return reasons.filter(Boolean).join(",");
  }
  function resolveOfficeSemanticMovementTarget(item, fallback) {
    const parts = officeSemanticMovementParts(item, fallback);
    const presetKey = officeMovementPresetKeyForParts(parts.category, parts.sequenceKey, parts.status, parts.fieldStates, parts.queue);
    const anchorName = officeMovementAnchorNameForParts(parts.category, presetKey, parts.sequenceKey);
    const anchor = office19thCenturyAnchor(anchorName);
    const preset = OFFICE_ANIMATION_PRESETS[presetKey] || OFFICE_ANIMATION_PRESETS.generic_tool_work;
    const fallbackReason = officeMovementFallbackReason(parts, presetKey);
    return {
      presetKey,
      zoneId: anchor.zoneKey || preset.zoneId || "",
      anchorId: anchor.key || preset.anchorId || presetKey,
      targetZone: anchor.targetZoneKey || preset.targetZone || "",
      targetObject: anchor.targetObjectKey || preset.targetObject || "",
      targetKey: anchor.targetObjectKey || preset.targetKey || "",
      targetProp: anchor.propGroupKey || preset.targetProp || "",
      fallbackReason,
      employeeId: parts.employeeId,
      instanceId: parts.instanceId,
      runId: parts.runId || "",
      threadId: parts.threadId || "",
    };
  }
  function officeMovementSequencePreset(interactionPose, interactionPropState, interactionLabel) {
    return Object.freeze({
      steps: Object.freeze(["receive_event", "show_activity_bubble", "move_to_target_anchor", "perform_short_interaction", "return_or_queue_next"]),
      interactionPose,
      interactionPropState,
      interactionLabel,
    });
  }
  const OFFICE_MOVEMENT_SEQUENCE_PRESETS = Object.freeze({
    action_center_activity: officeMovementSequencePreset("checking_dispatch_board", "dispatch_board_check", "Check dispatch board"),
    project_context_read: officeMovementSequencePreset("reading_archive", "cabinet_ledger_open", "Read cabinet ledger"),
    source_review: officeMovementSequencePreset("reading_archive", "archive_bundle_open", "Review archive bundle"),
    ticket_work_package: officeMovementSequencePreset("stamping_ticket_card", "ticket_card_stamped", "Stamp ticket card"),
    document_note_email: officeMovementSequencePreset("typing_mail_station", "typewriter_active", "Type at mail station"),
    tool_call: officeMovementSequencePreset("working_machine", "machine_lever_active", "Work machine"),
    command_execution: officeMovementSequencePreset("working_machine", "machine_press_active", "Run press"),
    test_check: officeMovementSequencePreset("working_machine", "gauge_check_active", "Check machine gauge"),
    planning_thinking: officeMovementSequencePreset("sketching_drafting_table", "blueprint_marked", "Sketch at drafting table"),
    generic_tool_work: officeMovementSequencePreset("generic_fallback_work", "workbench_active", "Generic workbench task"),
    restricted_missing_stale: officeMovementSequencePreset("generic_fallback_work", "limited_payload_badge", "Inspect limited payload"),
    delegation: Object.freeze({
      steps: Object.freeze(["source_delegation_state", "move_to_dispatch_handoff_point", "target_acknowledges", "conference_folder_card_handoff", "target_moves_to_next_station", "completion_handback_outbox_stamp"]),
      interactionPose: "delegation_conference",
      interactionPropState: "folder_card_handoff",
      interactionLabel: "Delegation folder handoff",
    }),
    completed: officeMovementSequencePreset("stamping_ticket_card", "completed_stamp_tray", "Stamp completed card"),
  });
  function officeMovementSequencePresetForTarget(movementTarget) {
    const key = movementTarget && movementTarget.presetKey;
    if (key === "idle_available") return null;
    return OFFICE_MOVEMENT_SEQUENCE_PRESETS[key] || OFFICE_MOVEMENT_SEQUENCE_PRESETS.generic_tool_work;
  }
  function officePresetKeyForParts(category, sequenceKey, status) {
    const cleanStatus = norm(status);
    const cleanSequence = norm(sequenceKey);
    const cleanCategory = norm(category);
    if (cleanCategory && OFFICE_ANIMATION_PRESET_ALIAS[cleanCategory]) return OFFICE_ANIMATION_PRESET_ALIAS[cleanCategory];
    if (cleanSequence && OFFICE_ANIMATION_PRESET_ALIAS[cleanSequence]) return OFFICE_ANIMATION_PRESET_ALIAS[cleanSequence];
    if (cleanCategory.includes("action_center") || cleanCategory.includes("dispatch") || cleanCategory.includes("intake") || cleanCategory.includes("approval") || cleanCategory.includes("question")) return "action_center_activity";
    if (cleanCategory.includes("source") || cleanCategory.includes("archive_review")) return "source_review";
    if (cleanCategory.includes("work_package") || cleanCategory.includes("ticket")) return "ticket_work_package";
    if (cleanCategory.includes("project") || cleanCategory.includes("context") || cleanCategory.includes("knowledge") || cleanCategory.includes("okf")) return "project_context_read";
    if (cleanCategory.includes("note") || cleanCategory.includes("document") || cleanCategory.includes("email") || cleanCategory.includes("mail")) return "document_note_email";
    if (cleanCategory.includes("delegat") || cleanSequence.includes("delegat")) return "delegation";
    if (cleanCategory.includes("command") || cleanCategory.includes("terminal") || cleanCategory.includes("shell") || cleanCategory.includes("exec")) return "command_execution";
    if (cleanCategory.includes("test") || cleanCategory.includes("verification")) return "test_check";
    if (cleanCategory.includes("plan") || cleanCategory.includes("thinking") || cleanCategory.includes("checking_files") || cleanCategory.includes("sketch") || cleanCategory.includes("design") || cleanCategory.includes("drafting") || cleanCategory.includes("blueprint")) return "planning_thinking";
    if (cleanCategory.includes("tool")) return "tool_call";
    if (cleanStatus.includes("restricted") || cleanStatus.includes("missing") || cleanStatus.includes("stale")) return "restricted_missing_stale";
    if (cleanStatus.includes("fail") || cleanStatus.includes("error") || cleanSequence === "failed") return "failed";
    if (cleanStatus.includes("block") || cleanStatus.includes("need") || cleanSequence === "blocked") return "blocked";
    if (cleanStatus.includes("complete") || cleanStatus.includes("done") || cleanSequence === "completed") return "completed";
    if (cleanSequence.includes("idle")) return "idle_available";
    return "generic_tool_work";
  }
  function officeActivityBubbleVariantForParts(category, sequenceKey, status, fieldStates, queue) {
    const presetKey = officePresetKeyForParts(category, sequenceKey, status);
    const preset = OFFICE_ANIMATION_PRESETS[presetKey] || OFFICE_ANIMATION_PRESETS.generic_tool_work;
    const statusKey = officeStatusKeyForParts(status || officeRestrictedStatusFromCategory(category) || (preset.tone === "completed" ? "completed" : preset.tone), fieldStates, queue);
    const statusVariant = OFFICE_ACTIVITY_STATUS_VARIANTS[statusKey] || OFFICE_ACTIVITY_STATUS_VARIANTS.running;
    return {
      ...preset,
      statusKey: statusVariant.key,
      statusLabel: statusVariant.label,
      statusIcon: statusVariant.icon,
      statusTone: statusVariant.tone,
      bubbleLabel: preset.label || "WORK",
    };
  }
  function officePresetForIntent(intent) {
    const refs = intent && intent.backingReferences;
    const key = officePresetKeyForParts(
      intent && ((refs && refs.semanticCategory) || intent.category),
      intent && intent.sequenceKey,
      intent && ((refs && refs.semanticStatus) || intent.status)
    );
    return OFFICE_ANIMATION_PRESETS[key] || OFFICE_ANIMATION_PRESETS.generic_tool_work;
  }
  function officeActivityBubbleVariantForIntent(intent) {
    const refs = intent && intent.backingReferences;
    return officeActivityBubbleVariantForParts(
      intent && ((refs && refs.semanticCategory) || intent.category),
      intent && intent.sequenceKey,
      intent && ((refs && refs.semanticStatus) || intent.status),
      null,
      intent && intent.queue
    );
  }
  function officePresetForWork(item) {
    const semantic = (item && item.semanticEvent) || {};
    const intent = (item && item.animationIntent) || {};
    const key = officePresetKeyForParts(semantic.category || item && item.category || intent.category, intent.sequenceKey, semantic.status || item && (item.status || item.state) || intent.status);
    return OFFICE_ANIMATION_PRESETS[key] || OFFICE_ANIMATION_PRESETS.generic_tool_work;
  }
  function officeActivityBubbleVariantForWork(item) {
    const semantic = (item && item.semanticEvent) || {};
    const intent = (item && item.animationIntent) || {};
    return officeActivityBubbleVariantForParts(
      semantic.category || item && item.category || intent.category,
      intent.sequenceKey,
      semantic.status || item && (item.status || item.state) || intent.status,
      item && item.fieldStates,
      item && item.queue
    );
  }
  const MAX_OFFICE_ACTIVE_MOTION = 4;
  const MAX_OFFICE_ANIMATION_QUEUE_PER_EMPLOYEE = 6;
  const MAX_OFFICE_VISIBLE_COMPRESSED_MARKERS = 4;
  const MAX_OFFICE_VISIBLE_DELEGATION_HANDOFFS = 4;
  const MAX_OFFICE_VISIBLE_WORK_CHIPS = 3;
  const MAX_OFFICE_VISIBLE_SPEECH_BUBBLES = 2;
  const MAX_OFFICE_RAIL_ITEMS = 80;
  const OFFICE_SEQUENCE_FALLBACK_MS = 1300;
  const OFFICE_TRAVEL_MS = 900;
  const OFFICE_STATION_HOLD_MS = 1150;
  const OFFICE_GRID_WALK_BASE_MS = 520;
  const OFFICE_GRID_WALK_CELL_MS = 520;
  const OFFICE_ZONE_MOTION = {
    dispatch: { x: -8, y: 4, turn: -2 },
    dispatch_board: { x: -8, y: 4, turn: -2 },
    factory: { x: 10, y: -3, turn: 2 },
    factory_floor: { x: 10, y: -3, turn: 2 },
    project: { x: 8, y: 5, turn: 1 },
    project_office: { x: 8, y: 5, turn: 1 },
    archive: { x: 6, y: -6, turn: -1 },
    inbox_outbox: { x: -6, y: -5, turn: -1 },
    handoff: { x: 12, y: 2, turn: 3 },
    inspection: { x: 0, y: -6, turn: 0 },
    recent_history: { x: 0, y: 3, turn: 0 },
    common: { x: 0, y: 2, turn: 0 },
    common_area: { x: 0, y: 2, turn: 0 },
  };
  function clampOfficeAnimationDuration(value) {
    const duration = Number(value);
    const base = !Number.isFinite(duration) || duration <= 0 ? OFFICE_SEQUENCE_FALLBACK_MS : duration;
    return Math.min(7600, Math.max(OFFICE_TRAVEL_MS + OFFICE_STATION_HOLD_MS, base + OFFICE_STATION_HOLD_MS));
  }
  function clampOfficeTravelDuration(value) {
    const duration = Number(value);
    if (!Number.isFinite(duration) || duration <= 0) return OFFICE_TRAVEL_MS;
    return Math.min(5600, Math.max(520, duration));
  }
  function officeZoneCenter(zoneId) {
    return OFFICE_ZONE_GRID_CENTERS[norm(zoneId)] || null;
  }
  function officeZoneGridDistance(sourceZoneId, targetZoneId) {
    const source = officeZoneCenter(sourceZoneId);
    const target = officeZoneCenter(targetZoneId);
    if (!source || !target) return 1;
    return Math.hypot(target.x - source.x, target.y - source.y);
  }
  function officeTravelDurationForZones(sourceZoneId, targetZoneId) {
    const distance = officeZoneGridDistance(sourceZoneId, targetZoneId);
    return clampOfficeTravelDuration(OFFICE_GRID_WALK_BASE_MS + (distance * OFFICE_GRID_WALK_CELL_MS));
  }
  function officePlaybackDuration(travelMs, stationMs = OFFICE_STATION_HOLD_MS) {
    return Math.min(8200, Math.max(OFFICE_TRAVEL_MS + stationMs, Number(travelMs) + stationMs));
  }
  function stableOffset(value, spread) {
    const text = String(value || "");
    let hash = 0;
    for (let index = 0; index < text.length; index += 1) hash = (hash + text.charCodeAt(index) * (index + 1)) % 997;
    return (hash % (spread * 2 + 1)) - spread;
  }
  function animationIntentId(intent) {
    if (!intent) return "";
    const base = intent.id || intent.intentId;
    if (!base) return "";
    const refs = intent.backingReferences || {};
    const latestMessageId = refs.latestMessageId || (Array.isArray(intent.inspectableMessageIds) ? intent.inspectableMessageIds[0] : "");
    if (!latestMessageId || String(base).includes(`:${latestMessageId}`)) return base;
    if (String(base).startsWith("intent:idle:") || String(base).startsWith("intent:compressed:")) return base;
    return `${base}:${latestMessageId}`;
  }
  function animationIntentRunId(intent) {
    const refs = intent && intent.backingReferences;
    return (refs && refs.runId) || (Array.isArray(intent && intent.inspectableRunIds) ? intent.inspectableRunIds[0] : "");
  }
  function animationIntentCompletionState(intent, tone) {
    const completion = intent && (intent.completionState || intent.completion || intent.completedState);
    if (hasValue(completion)) return String(completion);
    if (intent && (intent.completedAt || intent.finishedAt)) return "completed";
    if (tone === "completed") return "completed";
    return "";
  }
  function animationIntentCancellationState(intent) {
    const cancellation = intent && (intent.cancellationState || intent.cancelledState || intent.canceledState || intent.cancelReason || intent.cancellationReason);
    if (hasValue(cancellation)) return String(cancellation);
    if (intent && (intent.cancelledAt || intent.canceledAt)) return "cancelled";
    return "";
  }
  function animationIntentSkippedState(intent, compression) {
    if (!intent) return "";
    if (intent.skipped || intent.isSkipped || (compression && (compression.skipped || compression.isSkipped))) return "skipped";
    if (intent.compressed || intent.isCompressed || (compression && compression.isCompressed)) return "compressed";
    return "";
  }
  function animationIntentStatus(intent) {
    const refs = intent && intent.backingReferences;
    return norm((refs && refs.semanticStatus) || (intent && (intent.status || intent.category || intent.sequenceKey)));
  }
  function animationIntentTone(intent) {
    const preset = officePresetForIntent(intent);
    const status = animationIntentStatus(intent);
    const sequence = norm(intent && intent.sequenceKey);
    if (status.includes("fail") || sequence === "failed") return "failed";
    if (status.includes("block") || status.includes("need") || sequence === "blocked") return "blocked";
    if (status.includes("queue")) return "queued";
    if (status.includes("complete") || status.includes("done") || sequence === "completed") return "completed";
    if (sequence.includes("delegat")) return "delegated";
    if (sequence.includes("idle")) return "idle";
    if (preset.tone === "fallback") return "fallback";
    return preset.tone || "running";
  }
  function animationIntentLabel(intent, preset) {
    const sequence = norm(intent && intent.sequenceKey);
    if (sequence === "idle_available") return "AVAILABLE";
    if (sequence === "completed") return "RECENT";
    if (sequence === "blocked") return "BLOCKED";
    if (sequence === "failed") return "FAILED";
    if (intent && intent.compression && intent.compression.isCompressed) return "COMPRESSED";
    if (sequence.includes("delegat")) return "HANDOFF";
    return (preset && preset.label) || "MOVING";
  }
  function animationIntentSortValue(intent) {
    const hints = (intent && intent.timingHints) || {};
    return [
      Number(intent && intent.queuePosition) || 0,
      Date.parse(hints.firstEventAt || hints.createdAt || hints.updatedAt || "") || 0,
      String(animationIntentId(intent) || "")
    ].join(":");
  }
  function normalizeOfficeAnimationIntent(intent, employeeId, source) {
    const id = animationIntentId(intent);
    if (!intent || !id) return null;
    const hints = intent.timingHints || {};
    const preset = officePresetForIntent(intent);
    const bubbleVariant = officeActivityBubbleVariantForIntent(intent);
    const movementTarget = resolveOfficeSemanticMovementTarget(intent, { employeeId, selectionId: id });
    const movementPreset = OFFICE_ANIMATION_PRESETS[movementTarget.presetKey] || preset;
    const sequencePreset = officeMovementSequencePresetForTarget(movementTarget);
    const targetZone = norm(intent.targetZone || intent.zone || movementTarget.targetZone || preset.targetZone || "");
    const motion = movementPreset.motion || preset.motion || OFFICE_ZONE_MOTION[targetZone] || OFFICE_ZONE_MOTION[norm(intent.sequenceKey)] || OFFICE_ZONE_MOTION.factory_floor;
    const compressedCount = Number(intent.compression && intent.compression.compressedCount) || 0;
    const compression = intent.compression || {};
    const status = animationIntentStatus(intent);
    const tone = animationIntentTone(intent);
    const rawCategory = intent.category || (intent.backingReferences && intent.backingReferences.semanticCategory) || "";
    return {
      id: String(id),
      employeeId: String(intent.sourceEmployeeId || employeeId || (intent.backingReferences && intent.backingReferences.employeeId) || ""),
      targetEmployeeId: intent.targetEmployeeId || "",
      runId: animationIntentRunId(intent),
      label: animationIntentLabel(intent, preset),
      tone,
      status,
      interruptibility: intent.interruptibility || "",
      category: preset.key,
      rawCategory,
      sequenceKey: preset.key,
      rawSequenceKey: intent.sequenceKey || "",
      presetKey: preset.key,
      presetLabel: preset.sceneLabel,
      presetIcon: preset.icon,
      presetObjectClass: preset.objectClass,
      presetAmbientOnly: !!preset.ambientOnly,
      presetBubbleLabel: bubbleVariant.bubbleLabel,
      presetStatusKey: bubbleVariant.statusKey,
      presetStatusLabel: bubbleVariant.statusLabel,
      presetStatusIcon: bubbleVariant.statusIcon,
      presetStatusTone: bubbleVariant.statusTone,
      movementZoneId: movementTarget.zoneId,
      sourceZoneId: "",
      movementAnchorId: movementTarget.anchorId,
      movementTargetKey: movementTarget.targetKey,
      movementTargetProp: movementTarget.targetProp,
      movementFallbackReason: movementTarget.fallbackReason,
      movementSequencePresetKey: movementTarget.presetKey,
      movementSequenceSteps: sequencePreset ? sequencePreset.steps : [],
      movementInteractionPose: sequencePreset ? sequencePreset.interactionPose : "",
      movementInteractionPropState: sequencePreset ? sequencePreset.interactionPropState : "",
      movementInteractionLabel: sequencePreset ? sequencePreset.interactionLabel : "",
      movementReturnMode: Number(intent.queuePosition) > 0 ? "next_queued_anchor" : "home_station",
      targetZone: intent.targetZone || movementTarget.targetZone || preset.targetZone || "",
      targetObject: intent.targetObject || movementTarget.targetObject || preset.targetObject || "",
      queuePosition: Number(intent.queuePosition) || 0,
      durationMs: clampOfficeAnimationDuration(hints.suggestedDurationMs),
      travelMs: clampOfficeTravelDuration(hints.suggestedTravelMs || hints.travelMs),
      delayMs: Math.min(900, Math.max(0, Number(hints.suggestedDelayMs) || 0)),
      updatedAt: hints.updatedAt || hints.lastEventAt || hints.createdAt || "",
      timingKey: [hints.createdAt, hints.firstEventAt, hints.lastEventAt, hints.updatedAt, hints.suggestedDurationMs, hints.suggestedDelayMs].map((value) => value || "").join("|"),
      sortValue: animationIntentSortValue(intent),
      compressedCount,
      compressionKey: compression.compressionKey || "",
      isCompressed: !!compression.isCompressed,
      skippedState: animationIntentSkippedState(intent, compression),
      completionState: animationIntentCompletionState(intent, tone),
      cancellationState: animationIntentCancellationState(intent),
      currentStep: intent.currentStep || intent.step || source || "",
      timingHints: { ...hints },
      isOverlay: !!preset.nonDroppable || ["blocked", "failed"].includes(tone) || norm(intent.interruptibility) === "non_interruptible",
      inspectableRunIds: Array.isArray(intent.inspectableRunIds) ? intent.inspectableRunIds : [],
      source,
      x: motion.x + stableOffset(id, 2),
      y: motion.y + stableOffset(`${id}:y`, 2),
      turn: motion.turn,
    };
  }
  function officeAnimationIntentVersion(intent) {
    return JSON.stringify({
      employeeId: intent.employeeId,
      id: intent.id,
      runId: intent.runId,
      label: intent.label,
      tone: intent.tone,
      status: intent.status,
      interruptibility: intent.interruptibility,
      category: intent.category,
      rawCategory: intent.rawCategory,
      sequenceKey: intent.sequenceKey,
      rawSequenceKey: intent.rawSequenceKey,
      presetKey: intent.presetKey,
      presetLabel: intent.presetLabel,
      presetIcon: intent.presetIcon,
      presetObjectClass: intent.presetObjectClass,
      presetAmbientOnly: intent.presetAmbientOnly,
      presetBubbleLabel: intent.presetBubbleLabel,
      presetStatusKey: intent.presetStatusKey,
      presetStatusLabel: intent.presetStatusLabel,
      presetStatusIcon: intent.presetStatusIcon,
      presetStatusTone: intent.presetStatusTone,
      movementZoneId: intent.movementZoneId,
      sourceZoneId: intent.sourceZoneId,
      movementAnchorId: intent.movementAnchorId,
      movementTargetKey: intent.movementTargetKey,
      movementTargetProp: intent.movementTargetProp,
      movementFallbackReason: intent.movementFallbackReason,
      movementSequencePresetKey: intent.movementSequencePresetKey,
      movementSequenceSteps: intent.movementSequenceSteps,
      movementInteractionPose: intent.movementInteractionPose,
      movementInteractionPropState: intent.movementInteractionPropState,
      movementInteractionLabel: intent.movementInteractionLabel,
      movementReturnMode: intent.movementReturnMode,
      targetZone: intent.targetZone,
      targetObject: intent.targetObject,
      queuePosition: intent.queuePosition,
      durationMs: intent.durationMs,
      travelMs: intent.travelMs,
      delayMs: intent.delayMs,
      updatedAt: intent.updatedAt,
      timingKey: intent.timingKey,
      sortValue: intent.sortValue,
      compressedCount: intent.compressedCount,
      compressionKey: intent.compressionKey,
      isCompressed: intent.isCompressed,
      skippedState: intent.skippedState,
      completionState: intent.completionState,
      cancellationState: intent.cancellationState,
      currentStep: intent.currentStep,
      timingHints: intent.timingHints,
      isOverlay: intent.isOverlay,
      inspectableRunIds: intent.inspectableRunIds,
      source: intent.source,
      x: intent.x,
      y: intent.y,
      turn: intent.turn,
    });
  }
  function refreshOfficeAnimationIntent(previousIntent, nextIntent) {
    if (!nextIntent) return null;
    if (!previousIntent) return nextIntent;
    return {
      ...nextIntent,
      startedAt: previousIntent.startedAt,
      sourceZoneId: previousIntent.sourceZoneId,
      travelMs: previousIntent.travelMs,
      durationMs: previousIntent.durationMs,
      x: previousIntent.x,
      y: previousIntent.y,
      turn: previousIntent.turn,
    };
  }
  function defaultOfficeSourceZoneId(intent) {
    const preset = OFFICE_ANIMATION_PRESETS[intent && intent.presetKey] || null;
    const targetZone = norm(intent && intent.movementZoneId);
    if (preset && preset.zoneTone === "dispatch") return "reception_dispatch";
    if (preset && preset.zoneTone === "floor") return "factory_machines";
    if (preset && preset.zoneTone === "common") return "common_area";
    if (targetZone === "archive_source_room") return "project_rooms_cabinets";
    if (targetZone === "mail_typewriter_station") return "archive_source_room";
    if (targetZone === "ticket_planning_table" || targetZone === "drafting_table") return "project_rooms_cabinets";
    if (targetZone === "handoff_path_points") return "reception_dispatch";
    return "main_office_desks";
  }
  function officeIntentWithPlaybackTiming(intent, sourceZoneId) {
    if (!intent) return null;
    const source = norm(sourceZoneId) || defaultOfficeSourceZoneId(intent);
    const target = norm(intent.movementZoneId) || source;
    const travelMs = officeTravelDurationForZones(source, target);
    return {
      ...intent,
      sourceZoneId: source,
      travelMs,
      durationMs: officePlaybackDuration(travelMs),
    };
  }
  function collectOfficeAnimationIntents(snapshot, employees) {
    const byKey = new Map();
    const add = (intent, employeeId, source) => {
      const normalized = normalizeOfficeAnimationIntent(intent, employeeId, source);
      if (!normalized || !normalized.employeeId) return;
      const key = `${normalized.employeeId}:${normalized.id}`;
      if (!byKey.has(key)) byKey.set(key, normalized);
    };
    employees.forEach((employee) => {
      add(employee && employee.idleIntent, employee && employee.id, "idle");
      add(employee && employee.currentWork && employee.currentWork.animationIntent, employee && employee.id, "current");
      (Array.isArray(employee && employee.recentHistory) ? employee.recentHistory : []).forEach((item) => add(item && item.animationIntent, employee && employee.id, "recent"));
      (Array.isArray(employee && employee.compressedGroups) ? employee.compressedGroups : []).forEach((group) => add(group && group.animationIntent, employee && employee.id, "compressed"));
    });
    (Array.isArray(snapshot && snapshot.work) ? snapshot.work : []).forEach((item) => add(item && item.animationIntent, item && item.employeeId, "work"));
    return Array.from(byKey.values()).sort((a, b) => a.employeeId.localeCompare(b.employeeId) || a.sortValue.localeCompare(b.sortValue));
  }
  function reduceOfficeAnimationQueues(previous, intents, now) {
    const incomingByEmployee = new Map();
    intents.forEach((intent) => {
      if (!incomingByEmployee.has(intent.employeeId)) incomingByEmployee.set(intent.employeeId, []);
      incomingByEmployee.get(intent.employeeId).push(intent);
    });
    const employeeIds = new Set([...Object.keys(previous.employees || {}), ...incomingByEmployee.keys()]);
    const nextEmployees = {};
    employeeIds.forEach((employeeId) => {
      const previousState = (previous.employees && previous.employees[employeeId]) || {};
      const present = incomingByEmployee.get(employeeId) || [];
      const presentIds = new Set(present.map((intent) => intent.id));
      const presentById = new Map(present.map((intent) => [intent.id, intent]));
      const rememberedCompleted = new Set(Array.isArray(previousState.completedIds) ? previousState.completedIds.filter((id) => presentIds.has(id)) : []);
      const previousCurrentExpired = previousState.current
        ? now - Number(previousState.current.startedAt || now) >= Number(previousState.current.durationMs || 0)
        : false;
      if (previousCurrentExpired && previousState.current && previousState.current.id) rememberedCompleted.add(previousState.current.id);
      const previousCurrent = previousState.current && presentById.has(previousState.current.id) && !previousCurrentExpired
        ? refreshOfficeAnimationIntent(previousState.current, presentById.get(previousState.current.id))
        : previousState.current && !previousCurrentExpired
          ? previousState.current
          : null;
      const previousQueue = (Array.isArray(previousState.queue) ? previousState.queue : [])
        .filter((intent) => presentIds.has(intent.id) && intent.id !== (previousCurrent && previousCurrent.id))
        .map((intent) => refreshOfficeAnimationIntent(intent, presentById.get(intent.id)))
        .filter(Boolean);
      const queuedIds = new Set(previousQueue.map((intent) => intent.id));
      const newIntents = present.filter((intent) => intent.id !== (previousCurrent && previousCurrent.id) && !queuedIds.has(intent.id) && !rememberedCompleted.has(intent.id));
      const mergedQueue = [...previousQueue, ...newIntents].sort((a, b) => a.sortValue.localeCompare(b.sortValue));
      const sourceZoneForNext = previousCurrentExpired && previousState.current
        ? previousState.current.movementZoneId
        : previousState.lastZoneId;
      const nextQueuedCurrent = previousCurrent ? null : mergedQueue.shift() || null;
      const current = previousCurrent || officeIntentWithPlaybackTiming(nextQueuedCurrent, sourceZoneForNext) || null;
      const overlays = present.filter((intent) => intent.isOverlay);
      const disappearedCurrent = previousState.current && !previousCurrent && !presentIds.has(previousState.current.id) ? previousState.current : null;
      nextEmployees[employeeId] = {
        current: current ? { ...current, startedAt: current.startedAt || (now + (current.delayMs || 0)) } : null,
        queue: mergedQueue.slice(0, MAX_OFFICE_ANIMATION_QUEUE_PER_EMPLOYEE),
        collapsedCount: Math.max(0, mergedQueue.length - MAX_OFFICE_ANIMATION_QUEUE_PER_EMPLOYEE),
        completedIds: Array.from(rememberedCompleted),
        overlays,
        lastZoneId: current ? current.movementZoneId : (previousCurrentExpired && previousState.current ? previousState.current.movementZoneId : previousState.lastZoneId),
        lastReconciled: disappearedCurrent ? {
          id: disappearedCurrent.id,
          label: ["completed", "failed", "blocked"].includes(disappearedCurrent.tone) ? "completed" : "cancelled",
          at: now
        } : previousState.lastReconciled,
      };
    });
    return { employees: nextEmployees };
  }
  function useOfficeAnimationSequencer(snapshot, employees) {
    const [sequencer, setSequencer] = useState({ employees: {} });
    const intents = useMemo(() => collectOfficeAnimationIntents(snapshot, employees), [snapshot, employees]);
    const intentSignature = useMemo(() => intents.map(officeAnimationIntentVersion).join("|"), [intents]);
    useEffect(() => {
      setSequencer((previous) => reduceOfficeAnimationQueues(previous, intents, Date.now()));
    }, [intentSignature]);
    useEffect(() => {
      if (!Object.values(sequencer.employees || {}).some((state) => state.current)) return undefined;
      const timer = setInterval(() => {
        setSequencer((previous) => {
          let changed = false;
          const nextEmployees = {};
          const now = Date.now();
          Object.entries(previous.employees || {}).forEach(([employeeId, state]) => {
            const current = state.current;
            if (!current || now - Number(current.startedAt || now) < current.durationMs) {
              nextEmployees[employeeId] = state;
              return;
            }
            changed = true;
            const queue = Array.isArray(state.queue) ? state.queue.slice() : [];
            const rawNextCurrent = queue.shift() || null;
            const nextCurrent = officeIntentWithPlaybackTiming(rawNextCurrent, current.movementZoneId);
            nextEmployees[employeeId] = {
              ...state,
              current: nextCurrent ? { ...nextCurrent, startedAt: now + (nextCurrent.delayMs || 0) } : null,
              queue,
              collapsedCount: Math.max(0, (Number(state.collapsedCount) || 0) - 1),
              completedIds: [...(Array.isArray(state.completedIds) ? state.completedIds : []), current.id].slice(-20),
              lastZoneId: nextCurrent ? nextCurrent.movementZoneId : current.movementZoneId,
              lastReconciled: { id: current.id, label: "completed", at: now },
            };
          });
          return changed ? { employees: nextEmployees } : previous;
        });
      }, 240);
      return () => clearInterval(timer);
    }, [sequencer]);
    return sequencer;
  }
  function officeAnimationState(sequencer, employeeId, movingIds) {
    const state = sequencer && sequencer.employees && sequencer.employees[String(employeeId)];
    if (!state) return { queue: [], overlays: [] };
    const current = state.current || null;
    const moving = current && movingIds.has(current.id);
    return { ...state, current, moving, capped: !!(current && !moving) };
  }
  function normalizedWorkAnimationIntent(item, source) {
    return normalizeOfficeAnimationIntent(item && item.animationIntent, item && item.employeeId, source || "work");
  }
  function normalizedGroupAnimationIntent(group, employee, source) {
    return normalizeOfficeAnimationIntent(group && group.animationIntent, employee && employee.id, source || "compressed");
  }
  function animationIntentMatchesSelection(intent, target) {
    if (!intent || !target) return false;
    const selectedId = target.selection && String(target.selection.id || "");
    if (selectedId && String(intent.id) === selectedId) return true;
    const runId = String(intent.runId || "");
    if (target.work && runId && String(workRunId(target.work)) === runId) return true;
    if (target.work && String(intent.id || "") === String(workIntentId(target.work) || "")) return true;
    if (target.group && intent.compressionKey && String(intent.compressionKey) === String(target.group.compressionKey || "")) return true;
    const runIds = Array.isArray(intent.inspectableRunIds) ? intent.inspectableRunIds.map((id) => String(id)) : [];
    if (target.work && runIds.includes(String(workRunId(target.work) || ""))) return true;
    if (target.compressedWorkItems && target.compressedWorkItems.some((item) => runIds.includes(String(workRunId(item) || "")))) return true;
    return false;
  }
  function resolveTargetAnimationIntent(target, animationState) {
    const direct = target && (target.animationIntent || normalizedWorkAnimationIntent(target.work, "selected") || normalizedGroupAnimationIntent(target.group, target.employee, "selected-compressed"));
    const candidates = [
      animationState && animationState.current,
      ...(Array.isArray(animationState && animationState.queue) ? animationState.queue : []),
      ...(Array.isArray(animationState && animationState.overlays) ? animationState.overlays : []),
    ].filter(Boolean);
    const matched = candidates.find((intent) => animationIntentMatchesSelection(intent, target));
    return matched || direct || (target && target.kind === "employee" ? animationState && animationState.current : null);
  }
  function officeRailAnimationStatus(item, sequencer) {
    const intent = item && (item.animationIntent || normalizedWorkAnimationIntent(item.work, item.source) || normalizedGroupAnimationIntent(item.group, item.employee, item.source));
    const employeeId = item && item.employee && item.employee.id || intent && intent.employeeId;
    const state = employeeId ? officeAnimationState(sequencer, employeeId, new Set()) : null;
    const intentId = intent && intent.id;
    if (intent && intent.cancellationState) return "cancelled";
    if (intent && intent.skippedState === "skipped") return "skipped";
    if (state && state.current && intentId && state.current.id === intentId) return "playing";
    if (state && Array.isArray(state.queue) && state.queue.some((queued) => queued.id === intentId)) return "queued";
    if (state && Array.isArray(state.overlays) && state.overlays.some((overlay) => overlay.id === intentId)) return intent.tone || "blocked";
    if (intent && intent.isCompressed) return "compressed";
    const className = workStateClass(item && (item.work || item.group || item));
    if (["failed", "blocked", "restricted", "stale", "missing", "loading"].includes(className)) return className;
    if (className === "done" || intent && intent.completionState) return "completed";
    return norm(item && item.label) || "recent";
  }
  function officeRailItems(snapshot, employees) {
    const work = Array.isArray(snapshot && snapshot.work) ? snapshot.work : [];
    const items = [];
    const seen = new Set();
    const pushWork = (source, item, employee, label) => {
      const selectionId = workInstanceSelectionId(item, items.length);
      if (!selectionId) return;
      const key = `work:${selectionId}`;
      if (seen.has(key)) return;
      seen.add(key);
      items.push({
        kind: "work",
        id: selectionId,
        source,
        label,
        title: workTitle(item),
        category: workCategory(item),
        status: item && (item.status || item.state),
        updatedAt: latestWorkTime(item),
        work: item,
        employee,
        missingInstanceId: !workInstanceIdentity(item),
        animationIntent: normalizedWorkAnimationIntent(item, source),
      });
    };

    employees.forEach((employee) => {
      if (employee.currentWork) {
        pushWork("visible", workByRunId(snapshot, employee.currentWork.runId) || employee.currentWork, employee, "Visible");
      }
      (Array.isArray(employee.recentHistory) ? employee.recentHistory : []).forEach((ref) => {
        pushWork("recent", workByRunId(snapshot, ref.runId) || ref, employee, "Recent");
      });
      (Array.isArray(employee.compressedGroups) ? employee.compressedGroups : []).forEach((group, index) => {
        if (!group || Number(group.compressedCount) <= 1) return;
        const id = compressedSelectionId(employee.id, group, index);
        if (seen.has(`compressed:${id}`)) return;
        seen.add(`compressed:${id}`);
        const compressedWorkItems = compressedGroupWorkItems(snapshot, group);
        const bundle = officeCompressedBundleSummary(group, compressedWorkItems);
        items.push({
          kind: "compressed",
          id,
          source: "compressed",
          label: "Compressed",
          title: `${Number(group.compressedCount) || 0} ${bundle.visual.label.toLowerCase()}`,
          category: bundle.category,
          status: bundle.statusKey,
          updatedAt: group.lastEventAt,
          group,
          employee,
          bundle,
          animationIntent: normalizedGroupAnimationIntent(group, employee, "compressed"),
        });
      });
    });

    work.forEach((item) => {
      const employee = workEmployee(employees, item);
      const queue = item && item.queue;
      const label = queue && (queue.queued || queue.needsInput || queue.waitingForDelegates) ? "Queued" : IsActiveWork(item) ? "Visible" : "Recent";
      pushWork(label.toLowerCase(), item, employee, label);
    });

    return items
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
      .slice(0, MAX_OFFICE_RAIL_ITEMS);
  }
  function IsActiveWork(item) {
    const state = norm(item && (item.state || item.status));
    return state.includes("run") || state.includes("work") || state.includes("queue") || state.includes("delegat") || state.includes("input");
  }
  function selectedTarget(snapshot, employees, selection) {
    const selected = selection || defaultSelection(employees);
    if (!selected) return null;
    if (selected.kind === "employee") {
      const employee = employeeById(employees, selected.id);
      if (!employee) return { kind: "missing", selection: selected, title: "Selection no longer in snapshot" };
      const work = selectedOfficeWork(snapshot, employee);
      return { kind: "employee", selection: selected, employee, work, employees, animationIntent: normalizedWorkAnimationIntent(work, "employee"), title: employee.name || "AI employee" };
    }
    if (selected.kind === "work") {
      const work = workBySelectionId(snapshot, selected.id, employees);
      if (!work) return { kind: "missing", selection: selected, title: "Selected run no longer in snapshot" };
      const employee = workEmployee(employees, work);
      return { kind: "work", selection: selected, employee, work, employees, animationIntent: normalizedWorkAnimationIntent(work, "work"), missingInstanceId: !workInstanceIdentity(work), title: workTitle(work) };
    }
    if (selected.kind === "compressed") {
      const railItem = officeRailItems(snapshot, employees).find((item) => item.kind === "compressed" && String(item.id) === String(selected.id));
      const marker = compressedMarkers(snapshot, employees).find((item, index) => String(compressedSelectionId(item.employee && item.employee.id, item.group, index)) === String(selected.id));
      const group = (railItem && railItem.group) || (marker && marker.group);
      if (!group) return { kind: "missing", selection: selected, title: "Compressed queue marker no longer in snapshot" };
      const compressedWorkItems = compressedGroupWorkItems(snapshot, group);
      const employee = (railItem && railItem.employee) || (marker && marker.employee) || workEmployee(employees, compressedWorkItems[0]);
      const bundle = (railItem && railItem.bundle) || (marker && marker.bundle) || officeCompressedBundleSummary(group, compressedWorkItems);
      const title = (railItem && railItem.title) || `${Number(group.compressedCount) || compressedWorkItems.length || 0} ${bundle.visual.label.toLowerCase()}`;
      return { kind: "compressed", selection: selected, employee, group, compressedWorkItems, bundle, employees, animationIntent: normalizedGroupAnimationIntent(group, employee, "compressed"), title };
    }
    if (selected.kind === "zone") {
      const preset = OFFICE_ANIMATION_PRESETS[String(selected.id || "")] || selected.sceneObject || null;
      if (!preset) return { kind: "missing", selection: selected, title: "Zone object unavailable" };
      const work = workByPresetKey(snapshot, preset.key);
      const employee = workEmployee(employees, work);
      return { kind: "zone", selection: selected, preset, employee, work, employees, animationIntent: normalizedWorkAnimationIntent(work, "zone-object"), title: preset.sceneLabel || preset.label };
    }
    return { kind: "missing", selection: selected, title: "Selection unavailable" };
  }

  function OfficeBadge({ tone = "neutral", icon, children }) {
    return <span className={`office-badge ${tone}`}>{icon && <Icon name={icon} size={13} />}{children}</span>;
  }

  function OfficeSkeleton() {
    const treatment = officeStateTreatment("loading");
    return (
      <div
        className="office-scene-shell loading"
        data-appligator-id="office-state-loading"
        data-office-state-treatment={treatment.key}
        data-office-state-prop={treatment.prop}
        aria-label="Loading Office snapshot"
      >
        <div className="office-scene-caption">
          <span className="office-skeleton-line wide" />
          <span className="office-skeleton-line" />
        </div>
        <div className="office-zone-grid" aria-hidden="true">
          {["Dispatch", "Factory Floor", "Project Rooms", "Common Area"].map((label, index) => (
            <div key={label} className={`office-zone-skeleton z${index}`}>
              <span>{label}</span>
              <i />
              <i />
              <i />
            </div>
          ))}
        </div>
        <div className="office-drawer-skeleton" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
    );
  }

  function OfficeMessageState({ tone, icon, title, body }) {
    const treatment = officeStateTreatment(tone === "denied" ? "permission_denied_after_load" : tone === "empty" ? "empty" : "failed");
    return (
      <div
        className={`office-message-state ${tone || ""}`}
        data-appligator-id="office-message-state"
        data-office-state-treatment={treatment.key}
        data-office-state-prop={treatment.prop}
      >
        <span className="office-message-icon"><Icon name={icon || "alert"} size={22} /></span>
        <div>
          <strong>{title}</strong>
          <p>{body}</p>
        </div>
      </div>
    );
  }

  function OfficeSpeechBubbles({ employee, speechItems, hiddenCount, onSelect }) {
    const visible = Array.isArray(speechItems) ? speechItems : [];
    if (!visible.length) return null;
    return (
      <span className="office-speech-bubbles" data-appligator-id="office-safe-speech-bubbles" data-appligator-key={employee.id} aria-label={`${employee.name || "AI employee"} safe speech snippets`}>
        {visible.map(({ selectionId, speech }) => (
          <button
            type="button"
            key={selectionId}
            className={`office-speech-bubble ${speech.truncated ? "truncated" : ""}`}
            data-appligator-id="office-safe-speech-bubble"
            data-appligator-key={selectionId}
            data-office-speech-source={speech.source}
            data-office-speech-truncated={speech.truncated ? "true" : "false"}
            onClick={() => onSelect({ kind: "work", id: selectionId })}
            aria-label={`Safe speech snippet from ${employee.name || "AI employee"} work instance`}
          >
            {speech.text}
          </button>
        ))}
        {hiddenCount > 0 && <span className="office-speech-bubble more" aria-label={`${hiddenCount} additional speech snippets available in the inspector`}>+{hiddenCount}</span>}
      </span>
    );
  }

  function OfficeEmployeeTile({ employee, selected, onSelect, animation, delegationRole, workInstances }) {
    const stateClass = employeeStateClass(employee);
    const [stateLabel, icon] = stateDisplay(employee);
    const treatment = officeStateTreatment(stateClass);
    const currentWork = employee.currentWork;
    const groups = Array.isArray(employee.compressedGroups) ? employee.compressedGroups : [];
    const instances = Array.isArray(workInstances) ? workInstances : [];
    const compressedCount = groups.reduce((sum, group) => sum + Math.max(0, Number(group && group.compressedCount) || 0), 0);
    const currentIntent = animation && animation.current;
    const animationTone = currentIntent ? currentIntent.tone : stateClass === "idle" ? "idle" : stateClass;
    const queuedCount = (animation && Array.isArray(animation.queue) ? animation.queue.length : 0) + (Number(animation && animation.collapsedCount) || 0);
    const interactionPose = currentIntent && currentIntent.movementInteractionPose || "";
    const interactionPropState = currentIntent && currentIntent.movementInteractionPropState || "";
    const returnMode = currentIntent && (queuedCount > 0 ? "next_queued_anchor" : currentIntent.movementReturnMode) || "";
    const animationLabel = currentIntent
      ? animation.capped ? "QUEUED" : currentIntent.label
      : animation && animation.lastReconciled && Date.now() - Number(animation.lastReconciled.at || 0) < 3000
        ? animation.lastReconciled.label.toUpperCase()
        : stateClass === "idle" ? "AVAILABLE" : "HOLD";
    const overlays = Array.isArray(animation && animation.overlays) ? animation.overlays : [];
    const visualPreset = currentIntent
      ? currentIntent
      : stateClass === "idle"
        ? {
          presetKey: "idle_available",
          presetLabel: OFFICE_ANIMATION_PRESETS.idle_available.sceneLabel,
          presetIcon: OFFICE_ANIMATION_PRESETS.idle_available.icon,
          presetObjectClass: OFFICE_ANIMATION_PRESETS.idle_available.objectClass,
          presetBubbleLabel: OFFICE_ANIMATION_PRESETS.idle_available.label,
          presetStatusKey: "running",
          presetStatusLabel: "IDLE",
          presetStatusIcon: "clock",
          presetStatusTone: "idle",
          presetAmbientOnly: true,
          movementZoneId: "common_area",
          movementAnchorId: "idle_available",
          movementTargetKey: "idle_availability",
          movementTargetProp: "availabilityBench",
        }
        : null;
    const transformStyle = currentIntent && animation && animation.moving ? {
      "--office-animation-x": `${currentIntent.x}px`,
      "--office-animation-y": `${currentIntent.y}px`,
      "--office-animation-turn": `${currentIntent.turn}deg`,
      "--office-animation-duration": `${currentIntent.travelMs || currentIntent.durationMs}ms`,
    } : undefined;
    const subtitle = stateClass === "idle"
      ? "Available"
      : stateClass === "completed"
        ? "Recent work"
        : workTitle(currentWork);
    const speechItems = instances
      .map((instance) => ({ ...instance, speech: officeSpeechBubbleForWork(instance.item) }))
      .filter((instance) => instance.speech);
    const visibleSpeechItems = speechItems.slice(0, MAX_OFFICE_VISIBLE_SPEECH_BUBBLES);
    const primarySelection = employeePrimarySelection(employee, instances);
    const primarySelectionKind = primarySelection && primarySelection.kind;

    return (
      <div
        className={`office-employee-tile office-animation-tile ${stateClass} office-animation-${animationTone} ${interactionPose ? `office-sequence-pose-${interactionPose}` : ""} ${delegationRole ? `office-delegation-${delegationRole}` : ""} ${animation && animation.moving ? "office-animation-moving" : ""} ${animation && animation.capped ? "office-animation-capped" : ""} ${selected ? "selected" : ""}`}
        style={transformStyle}
        data-office-animation-intent={currentIntent ? currentIntent.id : ""}
        data-office-sequence-preset={currentIntent ? currentIntent.movementSequencePresetKey : ""}
        data-office-sequence-steps={currentIntent ? (currentIntent.movementSequenceSteps || []).join(">") : ""}
        data-office-interaction-pose={interactionPose}
        data-office-interaction-prop={interactionPropState}
        data-office-return-mode={returnMode}
        data-office-delegation-role={delegationRole || ""}
        data-office-state-treatment={treatment.key}
        data-office-state-prop={treatment.prop}
        title={`${employee.name}: ${stateLabel}`}
      >
        <button
          type="button"
          className="office-employee-main"
          data-appligator-id={`office-employee-${employee.id}`}
          data-appligator-key={employee.id}
          data-office-selection-kind={primarySelectionKind}
          onClick={() => onSelect(primarySelection)}
          aria-label={primarySelectionKind === "work" ? `Inspect ${employee.name || "AI employee"} active work instance` : `Inspect ${employee.name || "AI employee"} durable employee identity`}
        >
          <Avatar name={employee.name} avatarKey={employee.avatarKey} className="office-employee-avatar" />
          <span className="office-employee-copy">
            <strong>{employee.name || "AI employee"}</strong>
            <em>{subtitle}</em>
          </span>
          <span className={`office-state-badge ${stateClass}`}>
            <Icon name={icon} size={12} />
            {stateLabel}
          </span>
          <span
            className={`office-state-treatment ${treatment.tone}`}
            data-appligator-id="office-state-treatment"
            data-appligator-key={`${employee.id}:${treatment.key}`}
            data-office-state-treatment={treatment.key}
            data-office-state-prop={treatment.prop}
            aria-label={`${stateLabel} 19th-century treatment: ${treatment.label}`}
          >
            <Icon name={treatment.icon} size={9} />
            <b>{treatment.label}</b>
          </span>
          <span className={`office-animation-state-label ${animationTone}`} data-appligator-id={`office-animation-intent-${employee.id}`}>
            {animationLabel}
          </span>
          {visualPreset && (
            <span
              className={`office-preset-affordance office-activity-bubble office-preset-${visualPreset.presetKey} office-bubble-status-${visualPreset.presetStatusTone || animationTone} ${visualPreset.presetAmbientOnly ? "ambient-only" : ""}`}
              data-appligator-id={`office-animation-preset-${employee.id}`}
              data-appligator-key={employee.id}
              data-office-animation-preset={visualPreset.presetKey}
              data-office-bubble-variant={visualPreset.presetKey}
              data-office-bubble-status={visualPreset.presetStatusKey || animationTone}
              data-office-target-zone={visualPreset.movementZoneId || ""}
              data-office-target-anchor={visualPreset.movementAnchorId || ""}
              data-office-target-key={visualPreset.movementTargetKey || visualPreset.targetObject || ""}
              data-office-target-prop={visualPreset.movementTargetProp || ""}
              data-office-target-fallback={visualPreset.movementFallbackReason || ""}
              data-office-interaction-pose={visualPreset.movementInteractionPose || interactionPose}
              data-office-interaction-prop={visualPreset.movementInteractionPropState || interactionPropState}
              aria-label={`${visualPreset.presetBubbleLabel || visualPreset.presetLabel || visualPreset.presetKey} ${visualPreset.presetStatusLabel || animationLabel} activity bubble`}
            >
              <Icon name={visualPreset.presetIcon || "tool"} size={12} />
              <b>{visualPreset.presetAmbientOnly ? "" : (visualPreset.presetBubbleLabel || visualPreset.presetLabel || visualPreset.presetKey)}</b>
              {!visualPreset.presetAmbientOnly && (
                <em>
                  <Icon name={visualPreset.presetStatusIcon || icon} size={9} />
                  <span>{visualPreset.presetStatusLabel || animationLabel}</span>
                </em>
              )}
              <i />
            </span>
          )}
          {delegationRole && (
            <span className={`office-delegation-role-badge ${delegationRole}`} data-appligator-id={`office-delegation-role-${employee.id}`}>
              {delegationRole === "source" ? "SOURCE" : delegationRole === "target" ? "TARGET" : "HANDOFF"}
            </span>
          )}
          {overlays.length > 0 && (
            <span className="office-animation-overlays" aria-label="Preserved blocked or failed animation overlays">
              {overlays.slice(0, 2).map((overlay) => (
                <b
                  key={overlay.id}
                  className={overlay.tone}
                  data-appligator-id="office-animation-overlay"
                  data-appligator-key={overlay.id}
                >
                  {overlay.tone.toUpperCase()}
                </b>
              ))}
            </span>
          )}
          {(Number(employee.queueCount) > 0 || compressedCount > 1 || queuedCount > 0) && (
            <span className="office-mini-metrics">
              {Number(employee.queueCount) > 0 && <b>{employee.queueCount} queued</b>}
              {compressedCount > 1 && <b>{compressedCount} compressed</b>}
              {queuedCount > 0 && <b>{queuedCount} sequenced</b>}
            </span>
          )}
        </button>
        <OfficeSpeechBubbles employee={employee} speechItems={visibleSpeechItems} hiddenCount={Math.max(0, speechItems.length - visibleSpeechItems.length)} onSelect={onSelect} />
        {instances.length > 0 && (
          <span className="office-work-instance-group" data-appligator-id="office-work-instance-group" data-appligator-key={employee.id} aria-label={`${employee.name || "AI employee"} active work instances`}>
            {instances.slice(0, MAX_OFFICE_VISIBLE_WORK_CHIPS).map(({ item, selectionId, missingIdentity }, index) => {
              const bubbleVariant = officeActivityBubbleVariantForWork(item);
              const movementTarget = resolveOfficeSemanticMovementTarget(item, { employeeId: employee.id, selectionId });
              const instanceTreatment = officeStateTreatment(bubbleVariant.statusKey === "completed" ? "completed" : bubbleVariant.statusKey);
              return (
                <button
                  type="button"
                  key={selectionId}
                  className={`office-work-instance-chip office-activity-bubble-button ${workStateClass(item)} office-preset-${bubbleVariant.key} office-bubble-status-${bubbleVariant.statusTone} ${missingIdentity ? "missing-identity" : ""}`}
                  data-appligator-id={missingIdentity ? "office-missing-instance-id" : "office-work-instance-marker"}
                  data-appligator-key={selectionId}
                  data-office-employee-id={employee.id}
                  data-office-instance-id={workInstanceIdentity(item)}
                  data-office-run-id={workRunId(item) || ""}
                  data-office-thread-id={workThreadId(item) || ""}
                  data-office-bubble-variant={bubbleVariant.key}
                  data-office-bubble-status={bubbleVariant.statusKey}
                  data-office-state-treatment={instanceTreatment.key}
                  data-office-state-prop={instanceTreatment.prop}
                  data-office-target-zone={movementTarget.zoneId}
                  data-office-target-anchor={movementTarget.anchorId}
                  data-office-target-key={movementTarget.targetKey}
                  data-office-target-prop={movementTarget.targetProp}
                  data-office-target-fallback={movementTarget.fallbackReason}
                  onClick={() => onSelect({ kind: "work", id: selectionId })}
                  title={missingIdentity ? "Missing instance id; keeping this work item separate from employee identity" : `Inspect ${bubbleVariant.sceneLabel || bubbleVariant.bubbleLabel} activity`}
                  aria-label={`${bubbleVariant.bubbleLabel} ${bubbleVariant.statusLabel} activity ${index + 1}`}
                >
                  <Icon name={bubbleVariant.icon || "tool"} size={12} />
                  <b>{missingIdentity ? "MISSING" : bubbleVariant.bubbleLabel}</b>
                  <em><Icon name={bubbleVariant.statusIcon} size={9} />{bubbleVariant.statusLabel}</em>
                </button>
              );
            })}
            {instances.length > MAX_OFFICE_VISIBLE_WORK_CHIPS && <span className="office-work-instance-chip more">+{instances.length - MAX_OFFICE_VISIBLE_WORK_CHIPS}</span>}
          </span>
        )}
      </div>
    );
  }

  function OfficeZoneProps({ zone, snapshot, employees, onSelect }) {
    const propGroups = officeZonePropGroups(zone);
    if (!propGroups.length) return null;
    return (
      <div className="office-zone-props" data-appligator-id="office-19th-century-prop-groups" data-appligator-key={zone.key}>
        {propGroups.map((propGroup) => {
          const presets = presetsForPropGroup(propGroup);
          const associatedWorkCount = presets.flatMap((preset) => workItemsByPresetKey(snapshot, preset.key)).length;
          const selection = selectionForPropGroup(snapshot, employees, propGroup);
          return (
            <button
              type="button"
              key={propGroup.key}
              className="office-zone-prop"
              data-appligator-id="office-19th-century-prop-group"
              data-appligator-key={propGroup.key}
              data-office-zone-key={propGroup.zoneKey}
              data-office-associated-work-count={associatedWorkCount}
              data-office-selection-kind={selection.kind}
              data-office-visual-tokens={(propGroup.visualTokenKeys || []).map((tokenKey) => OFFICE_19TH_CENTURY_SCENE_CONFIG.visualTokens[tokenKey]).filter(Boolean).join(" ")}
              onClick={() => onSelect(selection)}
              title={`${humanizeConfigKey(propGroup.key)} prop group - inspect associated work or station state`}
            >
              <i aria-hidden="true" />
              <b>{humanizeConfigKey(propGroup.key)}</b>
            </button>
          );
        })}
      </div>
    );
  }

  function OfficeHandoffPathPoints({ snapshot, employees, onSelect }) {
    const pathPoints = office19thCenturyPathPoints();
    if (!pathPoints.length) return null;
    const selection = selectionForPresetObject(snapshot, employees, OFFICE_ANIMATION_PRESETS.delegation);
    return (
      <div className="office-19th-century-path" data-appligator-id="office-19th-century-handoff-path" aria-label="19th-century handoff path points">
        {pathPoints.map((point, index) => (
          <button
            type="button"
            key={point.key}
            className={`office-19th-century-path-point p${index + 1}`}
            data-appligator-id="office-19th-century-handoff-point"
            data-appligator-key={point.key}
            data-office-selection-kind={selection.kind}
            onClick={() => onSelect(selection)}
            title={`${point.label} - inspect delegation handoff activity`}
          >
            <i aria-hidden="true" />
            <b>{point.label}</b>
          </button>
        ))}
      </div>
    );
  }

  function OfficeZonePresetObjects({ zone, snapshot, employees, onSelect }) {
    const objects = officeZonePresetObjects(zone);
    if (!objects.length) return null;
    return (
      <div className="office-zone-affordances" data-appligator-id={`office-zone-affordances-${zone.key}`} data-appligator-key={zone.key} aria-label={`${zone.label} zone objects`}>
        {objects.map((preset) => {
          const associatedWorkItems = workItemsByPresetKey(snapshot, preset.key);
          const representativeWork = associatedWorkItems[0] || null;
          const selection = selectionForPresetObject(snapshot, employees, preset);
          return (
            <button
              type="button"
              key={preset.key}
              className={`office-zone-affordance office-preset-${preset.key} ${preset.objectClass || ""}`}
              data-appligator-id="office-zone-object"
              data-appligator-key={preset.key}
              data-office-animation-preset={preset.key}
              data-office-target-zone={preset.zoneId || ""}
              data-office-target-anchor={preset.anchorId || ""}
              data-office-target-object={preset.targetObject}
              data-office-run-id={workRunId(representativeWork) || ""}
              data-office-associated-work-count={associatedWorkItems.length}
              data-office-selection-kind={selection.kind}
              onClick={() => onSelect(selection)}
              title={`${preset.sceneLabel}${associatedWorkItems.length === 1 ? " - inspect matching work" : associatedWorkItems.length > 1 ? " - inspect grouped station work" : " - no matching work in snapshot"}`}
            >
              <Icon name={preset.icon || "tool"} size={13} />
              <b>{preset.sceneLabel}</b>
            </button>
          );
        })}
      </div>
    );
  }

  function OfficeZone({ layout, employees, selectedEmployeeId, onSelect, sequencer, movingIds, delegationHandoffs, snapshot }) {
    const zone = layout.zone;
    const bucket = layout.employeeBucket || "";
    const visibleEmployees = bucket ? zoneEmployees(employees, bucket, sequencer) : [];
    const zoneStyle = {
      "--office-zone-column": layout.column,
      "--office-zone-row": layout.row,
    };
    return (
      <section
        className={`office-zone ${zone.zoneTone} ${layout.scale || ""} ${visibleEmployees.length ? "has-employees" : "ambient"}`}
        style={zoneStyle}
        data-appligator-id="office-19th-century-zone"
        data-appligator-key={zone.key}
        data-office-zone-key={zone.key}
        data-office-zone-tone={zone.zoneTone}
      >
        <OfficeZoneProps zone={zone} snapshot={snapshot} employees={employees} onSelect={onSelect} />
        <OfficeZonePresetObjects zone={zone} snapshot={snapshot} employees={employees} onSelect={onSelect} />
        <div className="office-zone-heading">
          <span className="office-zone-label">{zone.label}</span>
          <strong>{layout.title}</strong>
          <em>{visibleEmployees.length} employee{visibleEmployees.length === 1 ? "" : "s"}</em>
        </div>
        <div className="office-zone-employees">
          {visibleEmployees.length ? visibleEmployees.map((employee) => (
            <OfficeEmployeeTile
              key={employee.id}
              employee={employee}
              selected={String(selectedEmployeeId) === String(employee.id)}
              onSelect={onSelect}
              animation={officeAnimationState(sequencer, employee.id, movingIds)}
              delegationRole={officeDelegationRole(employee.id, delegationHandoffs)}
              workInstances={employeeActiveWorkInstances(snapshot, employee)}
            />
          )) : <span className="office-zone-empty">{bucket ? "No employees in this zone" : "Props only"}</span>}
        </div>
      </section>
    );
  }

  function OfficeValue({ value, state = "missing", mono = false }) {
    if (!hasValue(value)) return <span className={`office-value-state ${fieldStateTone(state)}`}>{employeeStateLabel(state)}</span>;
    return <strong className={mono ? "mono" : ""}>{String(value)}</strong>;
  }
  function OfficePayloadRow({ label, value, state, mono }) {
    return (
      <div className="office-payload-row">
        <span>{label}</span>
        <OfficeValue value={value} state={state} mono={mono} />
      </div>
    );
  }
  function OfficeFieldStates({ fields }) {
    const missing = (Array.isArray(fields) ? fields : []).filter((field) => field && norm(field.state) !== "available");
    return (
      <div className="office-field-states" data-appligator-id="office-field-states">
        <h4>Field states</h4>
        {missing.length ? missing.map((field, index) => (
          <div key={`${field.field || "field"}-${index}`} className={`office-field-state ${fieldStateTone(field.state)}`}>
            <strong>{fieldLabel(field.field)}</strong>
            <b>{employeeStateLabel(field.state)}</b>
            {field.reason && <em>{field.reason}</em>}
          </div>
        )) : (
          <div className="office-field-state available">
            <strong>Snapshot fields</strong>
            <b>Available</b>
            <em>No missing, stale, or loading field state was returned.</em>
          </div>
        )}
      </div>
    );
  }
  function OfficeRunLink({ employeeId, runId, children, onOpenActionCenterRun }) {
    if (!runId) return <span className="office-run-missing">Missing run link</span>;
    if (!onOpenActionCenterRun) return <span className="office-run-missing">Open unavailable</span>;
    return (
      <button
        type="button"
        className="office-run-link"
        data-appligator-id={`office-open-run-${runId}`}
        data-appligator-key={runId}
        onClick={() => onOpenActionCenterRun(employeeId || "", runId)}
        title={`Open read-only Action Center run ${runId}`}
      >
        <Icon name="external" size={12} />
        {children || "Open run"}
      </button>
    );
  }
  function OfficeDrawerSection({ title, children, dataAppligatorId }) {
    return (
      <section className="office-drawer-section" data-appligator-id={dataAppligatorId || undefined}>
        <h4>{title}</h4>
        {children}
      </section>
    );
  }
  function OfficeCompressedWorkItems({ items, onOpenActionCenterRun }) {
    const compressedItems = Array.isArray(items) ? items : [];
    if (!compressedItems.length) {
      return (
        <div className="office-field-state missing">
          <strong>Compressed work payloads</strong>
          <b>Missing</b>
          <em>The compressed group did not resolve to underlying work records in this snapshot.</em>
        </div>
      );
    }
    return (
      <div className="office-compressed-work-list" data-appligator-id="office-compressed-work-payloads">
        {compressedItems.map((item, index) => {
          const semantic = (item && item.semanticEvent) || {};
          const queue = item && item.queue;
          const event = (item && item.event) || {};
          const runId = workRunId(item);
          return (
            <article
              key={runId || index}
              className="office-compressed-work-item"
              data-appligator-id="office-compressed-work-item"
              data-appligator-key={runId || index}
            >
              <div className="office-compressed-work-head">
                <strong>{workTitle(item)}</strong>
                <span className={`office-state-badge ${workStateClass(item)}`}>{employeeStateLabel(item && (item.status || item.state))}</span>
              </div>
              <OfficePayloadRow label="Run id" value={runId} mono />
              <OfficePayloadRow label="Semantic" value={semantic.category || item.category} />
              <OfficePayloadRow label="Status" value={semantic.status || item.status} />
              <OfficePayloadRow label="Queued" value={queue ? String(!!queue.queued) : null} state={queue ? "available" : "missing"} />
              <div className="office-payload-row">
                <span>Run inspection</span>
                <OfficeRunLink employeeId={item && item.employeeId} runId={runId} onOpenActionCenterRun={onOpenActionCenterRun}>Open read-only run</OfficeRunLink>
              </div>
              <div className="office-raw-text">{event.latestMessageText || semantic.rawBackingEventSummary || "Missing raw event text"}</div>
              <details className="office-debug-payload compact" open>
                <summary>Underlying work debug payload</summary>
                <pre>{JSON.stringify(debugPayload(item, null), null, 2)}</pre>
              </details>
            </article>
          );
        })}
      </div>
    );
  }
  function OfficeDelegationHandoffLayer({ handoffs, selectedKey, onSelect }) {
    const visible = Array.isArray(handoffs) ? handoffs : [];
    if (!visible.length) return null;
    return (
      <div className="office-delegation-handoffs" data-appligator-id="office-delegation-handoff-layer" aria-label="Delegation handoff choreography">
        {visible.map(({ work, info }, index) => {
          const runId = workRunId(work);
          const instanceId = workInstanceSelectionId(work, index) || workIntentId(work) || index;
          const key = selectionKey({ kind: "work", id: instanceId });
          const targetMissing = info.targetState !== "available";
          const childMissing = info.childRunState !== "available";
          const targetLocked = ["blocked", "failed"].includes(info.targetActiveState);
          const completedHandback = workStateClass(work) === "done";
          const stages = officeDelegationChoreographyStages(work, info);
          return (
            <button
              key={`${runId}-${index}`}
              type="button"
              className={`office-delegation-handoff ${targetMissing ? "missing-target" : ""} ${childMissing ? "missing-child" : ""} ${targetLocked ? "target-locked" : ""} ${completedHandback ? "completed-handback" : ""} ${info.sameEmployee ? "same-employee" : ""} ${selectedKey === key ? "selected" : ""}`}
              data-appligator-id={`office-delegation-handoff-${instanceId}`}
              data-appligator-key={instanceId}
              data-office-instance-id={workInstanceIdentity(work)}
              data-office-delegation-stage-count={stages.length}
              data-office-delegation-prop="folder_card_handoff"
              data-office-handback-target={completedHandback ? "outbox_stamp_board" : "pending"}
              data-source-employee-id={info.sourceEmployeeId || ""}
              data-target-employee-id={info.targetEmployeeId || ""}
              data-parent-run-id={info.parentRunId || ""}
              data-child-run-id={info.childRunId || ""}
              onClick={() => onSelect({ kind: "work", id: instanceId })}
              title={`Delegation handoff: ${info.handoffLabel}`}
            >
              <span className="office-handoff-person source" data-appligator-id="office-delegation-source-anchor" data-appligator-key={`${instanceId}:source`}>
                <b>{info.sourceEmployee ? info.sourceEmployee.name : "Source missing"}</b>
                <em>{info.sourceEmployeeId || "source id missing"}</em>
              </span>
              <span className="office-handoff-transfer" aria-hidden="true">
                <i />
                <Icon name="handover" size={15} />
                <strong>{childMissing ? "Child pending" : "Task card"}</strong>
              </span>
              <span className="office-handoff-person target" data-appligator-id="office-delegation-target-anchor" data-appligator-key={`${instanceId}:target`}>
                <b>{info.targetEmployee ? info.targetEmployee.name : "Target missing"}</b>
                <em>{targetMissing ? (info.targetEmployeeId || "target id missing") : `${info.targetActiveState || "ready"} -> ${info.nextZone}`}</em>
              </span>
              <span className="office-handoff-label">
                <b>{info.handoffLabel}</b>
                <em>{completedHandback ? "Stamped to outbox" : targetLocked ? "Target blocked/failed overlay preserved" : targetMissing ? "Missing target fallback" : "Delegation handoff"}</em>
              </span>
              <span className="office-handoff-stage-track" data-appligator-id="office-delegation-stage-track" data-appligator-key={instanceId}>
                {stages.map((stage) => (
                  <span
                    key={stage.key}
                    className={`office-handoff-stage ${stage.state}`}
                    data-appligator-id="office-delegation-stage"
                    data-appligator-key={`${instanceId}:${stage.key}`}
                    data-office-stage-key={stage.key}
                    data-office-stage-state={stage.state}
                    data-office-stage-prop={stage.prop}
                    title={`${stage.label}: ${stage.detail}`}
                  >
                    <b>{stage.label}</b>
                    <em>{stage.detail}</em>
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
    );
  }
  function OfficeInspectionDrawer({ target, onOpenActionCenterRun, sequencer }) {
    if (!target) return null;
    if (target.kind === "missing") {
      return (
        <aside className="office-inspection-drawer" data-appligator-id="office-inspection-drawer" aria-label="Office inspection drawer">
          <div className="office-drawer-head">
            <span className="office-drawer-kicker">Fallback state</span>
            <strong>{target.title}</strong>
            <em>The selected object disappeared after the latest snapshot refresh.</em>
          </div>
          <OfficeFieldStates fields={[{ field: "selection", state: "missing", reason: "The selected employee, run, queue marker, or completed action is absent from the current snapshot." }]} />
        </aside>
      );
    }

    const employee = target.employee;
    const work = target.work || null;
    const group = target.group || null;
    const compressedWorkItems = Array.isArray(target.compressedWorkItems) ? target.compressedWorkItems : [];
    const bundleSummary = target.bundle || (group ? officeCompressedBundleSummary(group, compressedWorkItems) : null);
    const currentWork = employee && employee.currentWork;
    const payload = group
      ? { compressedGroup: group, workItems: compressedWorkItems }
      : debugPayload(work, currentWork);
    const stateClass = work ? workStateClass(work) : employee ? employeeStateClass(employee) : workStateClass(group);
    const [stateLabel, icon] = employee ? stateDisplay(employee) : [employeeStateLabel(group && group.representativeStatus), "stack"];
    const animationState = employee ? officeAnimationState(sequencer, employee.id, new Set()) : null;
    const queuedAnimation = animationState && Array.isArray(animationState.queue) ? animationState.queue : [];
    const selectedAnimation = resolveTargetAnimationIntent(target, animationState);
    const selectedMovementTarget = work
      ? resolveOfficeSemanticMovementTarget(work, { employeeId: employee && employee.id, selectionId: target.selection && target.selection.id })
      : selectedAnimation ? {
        zoneId: selectedAnimation.movementZoneId,
        anchorId: selectedAnimation.movementAnchorId,
        targetZone: selectedAnimation.targetZone,
        targetObject: selectedAnimation.targetObject,
        targetKey: selectedAnimation.movementTargetKey,
        targetProp: selectedAnimation.movementTargetProp,
        fallbackReason: selectedAnimation.movementFallbackReason,
        instanceId: selectedAnimation.id,
        runId: selectedAnimation.runId,
      } : null;
    const semantic = (work && work.semanticEvent) || {};
    const queue = work && work.queue ? work.queue : null;
    const project = (work && work.project) || {};
    const timing = (work && work.timing) || {};
    const source = (work && work.source) || (semantic && semantic.sourceIdentifiers) || {};
    const tool = (work && work.tool) || {};
    const event = (work && work.event) || {};
    const delegation = (work && work.delegation) || {};
    const delegationInfo = isDelegationWork(work) ? officeDelegationInfo(work, target.employees || []) : null;
    const fieldStates = [
      ...(Array.isArray(employee && employee.fieldStates) ? employee.fieldStates : []),
      ...(Array.isArray(work && work.fieldStates) ? work.fieldStates : []),
      ...(delegationInfo && delegationInfo.sourceState !== "available" ? [{
        field: "delegation.sourceEmployee",
        state: delegationInfo.sourceState,
        reason: "The delegation source employee is not present in the current Office snapshot."
      }] : []),
      ...(delegationInfo && delegationInfo.targetState !== "available" ? [{
        field: "delegation.targetEmployee",
        state: delegationInfo.targetState,
        reason: delegationInfo.targetEmployeeId ? "The target employee id was returned, but that employee is not present in the current Office snapshot." : "The backend did not return a target employee id for this delegation event."
      }] : []),
      ...(delegationInfo && delegationInfo.childRunState !== "available" ? [{
        field: "delegation.childRun",
        state: delegationInfo.childRunState,
        reason: "The child run has not been created or loaded yet; no child run link is fabricated."
      }] : []),
      ...(target.kind === "compressed" && !queue ? [{
        field: "queue",
        state: "missing",
        reason: "Compressed groups do not expose one aggregate queue object; inspect the underlying work items for per-run queue state."
      }] : []),
      ...(target.kind === "zone" && !work ? [{
        field: "zoneObject.backingEvent",
        state: "missing",
        reason: "This scene object is inspectable, but no matching returned work payload is present in the current snapshot."
      }] : []),
      ...(target.missingInstanceId ? [{
        field: "work.instanceId",
        state: "missing",
        reason: "This work item did not include instanceId, runId, or threadId; it is kept as a separate missing-identity instance instead of being merged under employeeId."
      }] : []),
    ];
    const parentRunId = delegationInfo ? delegationInfo.parentRunId : delegation.parentRunId || semantic.parentRunId;
    const parentEmployeeId = delegationInfo ? delegationInfo.sourceEmployeeId : delegation.parentEmployeeId || semantic.parentEmployeeId;
    const childRunId = delegationInfo ? delegationInfo.childRunId : semantic.childRunId;
    const childEmployeeId = delegationInfo ? delegationInfo.targetEmployeeId : semantic.childEmployeeId;
    const runId = work && workRunId(work);
    const threadId = workThreadId(work);
    const selectedInstanceId = workInstanceIdentity(work);
    const selectedInstanceKind = work ? workInstanceIdentityKind(work) : target.kind === "compressed" ? "compressed" : "missing";
    const drawerTitle = target.kind === "compressed" ? "Compressed queue marker" : target.kind === "zone" ? target.title : work ? workTitle(work) : (employee && employee.name) || "Fallback state";

    return (
      <aside className="office-inspection-drawer" data-appligator-id="office-inspection-drawer" aria-label="Office inspection drawer">
        <div className="office-drawer-head">
          <span className="office-drawer-kicker">{target.kind === "employee" ? "Employee selection" : target.kind === "compressed" ? "Queue marker" : target.kind === "zone" ? "Zone object" : "Work action"}</span>
          <strong>{drawerTitle}</strong>
          <em>{employee ? `${employee.name || "AI employee"} - ${employee.role || "role missing"}` : "Employee missing"}</em>
          <span className={`office-state-badge ${stateClass}`}>
            <Icon name={icon} size={12} />
            {target.kind === "compressed" ? "COMPRESSED" : stateLabel}
          </span>
        </div>

        <OfficeFieldStates fields={fieldStates} />

        {target.kind === "zone" && (
          <OfficeDrawerSection title="Zone object">
            <OfficePayloadRow label="Object" value={target.preset && target.preset.sceneLabel} />
            <OfficePayloadRow label="Preset" value={target.preset && target.preset.key} />
            <OfficePayloadRow label="Target zone" value={target.preset && target.preset.targetZone} />
            <OfficePayloadRow label="Target object" value={target.preset && target.preset.targetObject} />
            <OfficePayloadRow label="Backing work" value={work && workTitle(work)} state={work ? "available" : "missing"} />
          </OfficeDrawerSection>
        )}

        <OfficeDrawerSection title="Durable employee identity" dataAppligatorId="office-employee-identity">
          <OfficePayloadRow label="Name" value={employee && employee.name} />
          <OfficePayloadRow label="Employee id" value={employee && employee.id} mono />
          <OfficePayloadRow label="Role" value={employee && employee.role} />
          <OfficePayloadRow label="State" value={employee && employee.state} />
          <OfficePayloadRow label="Queue count" value={employee ? Number(employee.queueCount) || 0 : null} />
          <OfficePayloadRow label="Last activity" value={employee && employee.lastActivityAt ? dateTimeLabel(employee.lastActivityAt) : null} state={employee && employee.lastActivityAt ? "available" : "missing"} />
        </OfficeDrawerSection>

        <OfficeDrawerSection title="Selected instance identity" dataAppligatorId="office-selected-instance-identity">
          <OfficePayloadRow label="Identity kind" value={selectedInstanceKind} state={selectedInstanceKind === "missing" ? "missing" : "available"} />
          <OfficePayloadRow label="Instance id" value={selectedInstanceId} state={selectedInstanceId ? "available" : "missing"} mono />
          <OfficePayloadRow label="Run id" value={runId || (currentWork && currentWork.runId)} state={(runId || (currentWork && currentWork.runId)) ? "available" : "missing"} mono />
          <OfficePayloadRow label="Thread id" value={threadId || (tool && tool.threadId)} state={(threadId || (tool && tool.threadId)) ? "available" : "missing"} mono />
          <OfficePayloadRow label="Employee grouping id" value={employee && employee.id} mono />
        </OfficeDrawerSection>

        <OfficeDrawerSection title="Selected action">
          <OfficePayloadRow label="Title" value={work ? work.title : currentWork && currentWork.title} />
          <OfficePayloadRow label="Detail" value={work && work.detail} />
          <OfficePayloadRow label="Status" value={(work && work.status) || (currentWork && currentWork.status)} />
          <OfficePayloadRow label="State" value={(work && work.state) || (currentWork && currentWork.state)} />
          <OfficePayloadRow label="Run id" value={runId || (currentWork && currentWork.runId)} mono />
          <div className="office-payload-row">
            <span>Run inspection</span>
            <OfficeRunLink employeeId={employee && employee.id} runId={runId || (currentWork && currentWork.runId)} onOpenActionCenterRun={onOpenActionCenterRun}>Open read-only run</OfficeRunLink>
          </div>
        </OfficeDrawerSection>

        <OfficeDrawerSection title="Semantic event">
          <OfficePayloadRow label="Category" value={semantic.category || (work && work.category) || (group && group.representativeCategory)} />
          <OfficePayloadRow label="Semantic status" value={semantic.status || (group && group.representativeStatus)} />
          <OfficePayloadRow label="Display label" value={semantic.displayLabel} />
          <OfficePayloadRow label="Raw summary" value={semantic.rawBackingEventSummary} />
          <OfficePayloadRow label="Inspectable" value={semantic.inspectability ? String(!!semantic.inspectability.inspectable) : null} />
        </OfficeDrawerSection>

        <OfficeDrawerSection title="Queue and compression">
          <OfficePayloadRow label="Queued" value={queue ? String(!!queue.queued) : null} state={queue ? "available" : "missing"} />
          <OfficePayloadRow label="Needs input" value={queue ? String(!!queue.needsInput) : null} state={queue ? "available" : "missing"} />
          <OfficePayloadRow label="Waiting delegates" value={queue ? String(!!queue.waitingForDelegates) : null} state={queue ? "available" : "missing"} />
          <OfficePayloadRow label="Approval request" value={queue && (queue.approvalRequestTitle || queue.approvalRequestId)} mono />
          <OfficePayloadRow label="Compressed count" value={(work && work.compression && work.compression.compressedCount) || (group && group.compressedCount)} />
          <OfficePayloadRow label="Compression key" value={(work && work.compression && work.compression.compressionKey) || (group && group.compressionKey)} mono />
        </OfficeDrawerSection>

        {target.kind === "compressed" && bundleSummary && (
          <OfficeDrawerSection title="Compressed bundle" dataAppligatorId="office-compressed-bundle-metadata">
            <OfficePayloadRow label="Bundle visual" value={bundleSummary.visual && bundleSummary.visual.label} />
            <OfficePayloadRow label="Bundle visual key" value={bundleSummary.visual && bundleSummary.visual.key} mono />
            <OfficePayloadRow label="Bundle status" value={bundleSummary.statusLabel} />
            <OfficePayloadRow label="Promoted states" value={bundleSummary.promotedStatusKeys.length ? bundleSummary.promotedStatusKeys.join(", ") : "none"} />
            <OfficePayloadRow label="Resolved underlying events" value={bundleSummary.resolvedItemCount} />
          </OfficeDrawerSection>
        )}

        {employee && (
          <OfficeDrawerSection title="Animation sequence">
            <div className="office-animation-debug" data-appligator-id="office-animation-sequencer-state">
              <div className="office-animation-metadata" data-appligator-id="office-animation-metadata">
                <OfficePayloadRow label="Intent id" value={selectedAnimation && selectedAnimation.id} mono />
                <OfficePayloadRow label="Category" value={selectedAnimation && selectedAnimation.category} />
                <OfficePayloadRow label="Sequence key" value={selectedAnimation && selectedAnimation.sequenceKey} />
                <OfficePayloadRow label="Preset name" value={selectedAnimation && selectedAnimation.presetKey} />
                <OfficePayloadRow label="Preset object" value={selectedAnimation && selectedAnimation.targetObject} />
                <OfficePayloadRow label="Current step" value={selectedAnimation && selectedAnimation.currentStep} />
                <OfficePayloadRow label="Queue position" value={selectedAnimation && selectedAnimation.queuePosition} />
                <OfficePayloadRow label="Raw category" value={selectedAnimation && selectedAnimation.rawCategory} />
                <OfficePayloadRow label="Raw sequence" value={selectedAnimation && selectedAnimation.rawSequenceKey} />
                <OfficePayloadRow label="Tone" value={selectedAnimation && selectedAnimation.tone} />
                <OfficePayloadRow label="Status" value={selectedAnimation && selectedAnimation.status} />
                <OfficePayloadRow label="Interruptibility" value={selectedAnimation && selectedAnimation.interruptibility} />
                <OfficePayloadRow label="Target zone" value={selectedAnimation && selectedAnimation.targetZone} />
                <OfficePayloadRow label="Target object" value={selectedAnimation && selectedAnimation.targetObject} />
                <OfficePayloadRow label="Movement zone id" value={selectedMovementTarget && selectedMovementTarget.zoneId} />
                <OfficePayloadRow label="Movement anchor id" value={selectedMovementTarget && selectedMovementTarget.anchorId} />
                <OfficePayloadRow label="Movement target key" value={selectedMovementTarget && selectedMovementTarget.targetKey} />
                <OfficePayloadRow label="Movement target prop" value={selectedMovementTarget && selectedMovementTarget.targetProp} />
                <OfficePayloadRow label="Movement fallback" value={selectedMovementTarget && selectedMovementTarget.fallbackReason} state={selectedMovementTarget && selectedMovementTarget.fallbackReason ? "available" : "missing"} />
                <OfficePayloadRow label="Sequence preset" value={selectedAnimation && selectedAnimation.movementSequencePresetKey} />
                <OfficePayloadRow label="Sequence steps" value={selectedAnimation && Array.isArray(selectedAnimation.movementSequenceSteps) ? selectedAnimation.movementSequenceSteps.join(" > ") : null} />
                <OfficePayloadRow label="Interaction pose" value={selectedAnimation && selectedAnimation.movementInteractionPose} />
                <OfficePayloadRow label="Interaction prop" value={selectedAnimation && selectedAnimation.movementInteractionPropState} />
                <OfficePayloadRow label="Return mode" value={selectedAnimation && selectedAnimation.movementReturnMode} />
                <OfficePayloadRow label="Compression key" value={selectedAnimation && selectedAnimation.compressionKey} mono />
                <OfficePayloadRow label="Skipped/compressed" value={selectedAnimation && (selectedAnimation.skippedState || (selectedAnimation.isCompressed ? "compressed" : ""))} state={selectedAnimation ? "available" : "missing"} />
                <OfficePayloadRow label="Cancellation" value={selectedAnimation && selectedAnimation.cancellationState} state={selectedAnimation && selectedAnimation.cancellationState ? "available" : "missing"} />
                <OfficePayloadRow label="Completion" value={selectedAnimation && selectedAnimation.completionState} state={selectedAnimation && selectedAnimation.completionState ? "available" : "loading"} />
                <OfficePayloadRow label="Timing hints" value={selectedAnimation && JSON.stringify(selectedAnimation.timingHints || {})} mono />
                <OfficePayloadRow label="Duration" value={selectedAnimation && selectedAnimation.durationMs} />
              </div>
              <OfficePayloadRow label="Motion capped" value={animationState ? String(!!animationState.capped) : null} state={animationState ? "available" : "missing"} />
              <OfficePayloadRow label="Queued intents" value={queuedAnimation.length + (Number(animationState && animationState.collapsedCount) || 0)} />
              <OfficePayloadRow label="Overlay count" value={Array.isArray(animationState && animationState.overlays) ? animationState.overlays.length : 0} />
              {queuedAnimation.length > 0 && (
                <div className="office-animation-queue" aria-label="Queued animation intents">
                  {queuedAnimation.slice(0, 4).map((intent) => (
                    <span
                      key={intent.id}
                      className={intent.tone}
                      data-appligator-id="office-animation-queued-intent"
                      data-appligator-key={intent.id}
                    >
                      <b>{intent.label}</b>
                      <em>{intent.sequenceKey}</em>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </OfficeDrawerSection>
        )}

        {target.kind === "compressed" && (
          <OfficeDrawerSection title="Compressed work item payloads">
            <OfficeCompressedWorkItems items={compressedWorkItems} onOpenActionCenterRun={onOpenActionCenterRun} />
          </OfficeDrawerSection>
        )}

        <OfficeDrawerSection title="Delegation">
          <OfficePayloadRow label="Handoff" value={delegationInfo ? delegationInfo.handoffLabel : delegation.handoffLabel} />
          <OfficePayloadRow label="Source employee" value={delegationInfo && delegationInfo.sourceEmployee && delegationInfo.sourceEmployee.name} state={delegationInfo ? delegationInfo.sourceState : "missing"} />
          <OfficePayloadRow label="Source employee id" value={delegationInfo && delegationInfo.sourceEmployeeId} state={delegationInfo ? delegationInfo.sourceState : "missing"} mono />
          <OfficePayloadRow label="Target employee" value={delegationInfo && delegationInfo.targetEmployee && delegationInfo.targetEmployee.name} state={delegationInfo ? delegationInfo.targetState : "missing"} />
          <OfficePayloadRow label="Target employee id" value={delegationInfo && delegationInfo.targetEmployeeId} state={delegationInfo ? delegationInfo.targetState : "missing"} mono />
          <OfficePayloadRow label="Target state" value={delegationInfo && delegationInfo.targetActiveState} state={delegationInfo && delegationInfo.targetEmployee ? "available" : "missing"} />
          <OfficePayloadRow label="Same employee" value={delegationInfo ? String(!!delegationInfo.sameEmployee) : null} state={delegationInfo ? "available" : "missing"} />
          <div className="office-payload-row">
            <span>Parent run</span>
            {parentRunId ? <OfficeRunLink employeeId={parentEmployeeId} runId={parentRunId} onOpenActionCenterRun={onOpenActionCenterRun}>Open parent</OfficeRunLink> : <OfficeValue value={null} state="missing" />}
          </div>
          <div className="office-payload-row">
            <span>Child run</span>
            {childRunId ? <OfficeRunLink employeeId={childEmployeeId} runId={childRunId} onOpenActionCenterRun={onOpenActionCenterRun}>Open child</OfficeRunLink> : <OfficeValue value={null} state={delegationInfo ? delegationInfo.childRunState : "missing"} />}
          </div>
        </OfficeDrawerSection>

        <OfficeDrawerSection title="Project and timing">
          <OfficePayloadRow label="Project" value={project.projectName || project.projectId} />
          <OfficePayloadRow label="Customer" value={project.customerName || project.customerId} />
          <OfficePayloadRow label="Created" value={timing.createdAt ? dateTimeLabel(timing.createdAt) : null} state={timing.createdAt ? "available" : "missing"} />
          <OfficePayloadRow label="Updated" value={(timing.updatedAt || (group && group.lastEventAt)) ? dateTimeLabel(timing.updatedAt || group.lastEventAt) : null} state={(timing.updatedAt || (group && group.lastEventAt)) ? "available" : "missing"} />
          <OfficePayloadRow label="Started" value={timing.startedAt ? dateTimeLabel(timing.startedAt) : null} state={timing.startedAt ? "available" : "loading"} />
          <OfficePayloadRow label="Finished" value={timing.finishedAt ? dateTimeLabel(timing.finishedAt) : null} state={timing.finishedAt ? "available" : "loading"} />
        </OfficeDrawerSection>

        <OfficeDrawerSection title="Durable identifiers">
          <OfficePayloadRow label="Source key" value={source.sourceKey} mono />
          <OfficePayloadRow label="Source id" value={source.sourceId} mono />
          <OfficePayloadRow label="Parent id" value={source.parentId} mono />
          <OfficePayloadRow label="Thread id" value={tool.threadId} mono />
          <OfficePayloadRow label="Session id" value={tool.sessionId} mono />
          <OfficePayloadRow label="Turn id" value={tool.turnId} mono />
          <OfficePayloadRow label="Instruction id" value={tool.instructionId} mono />
        </OfficeDrawerSection>

        <OfficeDrawerSection title="Raw event and tool event">
          <OfficePayloadRow label="Latest message id" value={event.latestMessageId} mono />
          <OfficePayloadRow label="Kind" value={event.latestMessageKind || source.latestMessageKind} />
          <OfficePayloadRow label="Role" value={event.latestMessageRole} />
          <OfficePayloadRow label="At" value={event.latestMessageAt ? dateTimeLabel(event.latestMessageAt) : null} state={event.latestMessageAt ? "available" : "missing"} />
          <div className="office-raw-text">{event.latestMessageText || semantic.rawBackingEventSummary || "Missing raw event text"}</div>
        </OfficeDrawerSection>

        {payload && (
          <details className="office-debug-payload" data-appligator-id="office-debug-payload" open>
            <summary>Returned debug payload</summary>
            <pre>{JSON.stringify(payload, null, 2)}</pre>
          </details>
        )}
        {!payload && (
          <div className="office-debug-payload missing" data-appligator-id="office-debug-payload-missing">
            <strong>Returned debug payload</strong>
            <span>Missing</span>
            <em>The snapshot did not include a raw debug payload for this selected target.</em>
          </div>
        )}
      </aside>
    );
  }

  function OfficeBottomRail({ snapshot, employees, selectedKey, onSelect, sequencer }) {
    const items = officeRailItems(snapshot, employees);
    const caps = snapshot && snapshot.caps;
    return (
      <div className="office-bottom-rail" data-appligator-id="office-bottom-queue-rail" aria-label="Office queued and recent activity rail">
        <div className="office-rail-meta">
          <strong>Queue rail</strong>
          <span>{caps ? `${caps.includedWorkCount} shown / ${caps.recentHistoryWindowMinutes}m or ${caps.recentHistoryMaxEvents} events` : "Backend capped"}</span>
        </div>
        <div className="office-rail-items">
          {items.length ? items.map((item) => {
            const key = selectionKey({ kind: item.kind === "compressed" ? "compressed" : "work", id: item.id });
            const handoff = item.work && isDelegationWork(item.work);
            const animationStatus = officeRailAnimationStatus(item, sequencer);
            const treatment = officeStateTreatment(animationStatus === "done" || animationStatus === "recent" ? "completed" : animationStatus === "needs" ? "blocked" : animationStatus === "error" ? "failed" : animationStatus);
            return (
              <button
                key={`${item.kind}-${item.id}`}
                type="button"
                className={`office-work-pill ${workStateClass(item.work || item.group || item)} office-rail-animation-${animationStatus} ${handoff ? "delegation-handoff" : ""} ${selectedKey === key ? "selected" : ""}`}
                data-appligator-id={`office-rail-${item.kind}-${item.id}`}
                data-appligator-key={item.id}
                data-office-handoff={handoff ? "delegation" : ""}
                data-office-instance-id={item.missingInstanceId ? "" : item.id}
                data-office-missing-instance-id={item.missingInstanceId ? "true" : "false"}
                data-office-animation-status={animationStatus}
                data-office-state-treatment={treatment.key}
                data-office-state-prop={treatment.prop}
                onClick={() => onSelect({ kind: item.kind === "compressed" ? "compressed" : "work", id: item.id })}
                title={`${item.label}: ${item.title}`}
              >
                <b>{item.title}</b>
                <em>{item.missingInstanceId ? "Missing instance id" : handoff ? "Handoff" : item.label}</em>
                <span
                  className={`office-rail-animation-status ${animationStatus}`}
                  data-appligator-id="office-rail-animation-status"
                  data-appligator-key={item.id}
                >
                  {employeeStateLabel(animationStatus)}
                </span>
              </button>
            );
          }) : <span className="office-rail-empty">No queued or recent activity returned</span>}
        </div>
      </div>
    );
  }

  function OfficeScene({ snapshot, onOpenActionCenterRun }) {
    const employees = orderedEmployees(snapshot);
    const sequencer = useOfficeAnimationSequencer(snapshot, employees);
    const [selection, setSelection] = useState(null);
    const target = selectedTarget(snapshot, employees, selection);
    const selectedKey = selectionKey((target && target.selection) || defaultSelection(employees));
    const activeEmployees = employees.filter((employee) => !["idle", "completed"].includes(employeeStateClass(employee))).length;
    const idleEmployees = employees.filter((employee) => employeeStateClass(employee) === "idle").length;
    const failedEmployees = employees.filter((employee) => ["blocked", "failed"].includes(employeeStateClass(employee))).length;
    const markers = compressedMarkers(snapshot, employees);
    const delegationHandoffs = officeDelegationHandoffs(snapshot, employees);
    const crowded = employees.length > 12;
    const movingIds = useMemo(() => {
      const ordered = employees
        .map((employee) => officeAnimationState(sequencer, employee.id, new Set()).current)
        .filter((intent) => intent && intent.tone !== "idle")
        .sort((a, b) => a.sortValue.localeCompare(b.sortValue))
        .slice(0, MAX_OFFICE_ACTIVE_MOTION)
        .map((intent) => intent.id);
      return new Set(ordered);
    }, [employees, sequencer]);
    const movingCount = movingIds.size;

    if (!employees.length) {
      const treatment = officeStateTreatment("empty");
      return (
        <div className="office-scene-shell empty" data-appligator-id="office-state-empty" data-office-state-treatment={treatment.key} data-office-state-prop={treatment.prop}>
          <OfficeMessageState
            tone="empty"
            icon="server"
            title="No AI employees on shift"
            body="The Office snapshot loaded, but no AI employees were returned for the global overview."
          />
        </div>
      );
    }

    return (
      <div
        className={`office-scene-shell ${crowded ? "crowded" : ""}`}
        data-appligator-id="appligator-office-scene"
        data-office-crowded={crowded ? "true" : "false"}
        data-office-motion-cap={MAX_OFFICE_ACTIVE_MOTION}
        data-office-compressed-marker-cap={MAX_OFFICE_VISIBLE_COMPRESSED_MARKERS}
        data-office-work-chip-cap={MAX_OFFICE_VISIBLE_WORK_CHIPS}
      >
        <div className="office-scene-caption">
          <strong>Global operations floor</strong>
          <span>All projects, all returned AI employees, read-only Action Center activity.</span>
        </div>
        <div className="office-scene-metrics" aria-label="Office snapshot metrics">
          <span><b>{employees.length}</b> employees</span>
          <span><b>{activeEmployees}</b> active</span>
          <span><b>{movingCount}</b> moving</span>
          <span><b>{idleEmployees}</b> idle</span>
          <span><b>{failedEmployees}</b> blocked/failed</span>
        </div>
        <div
          className="office-diorama-floor"
          data-appligator-id="office-19th-century-scene"
          data-office-scene-key={OFFICE_19TH_CENTURY_SCENE_CONFIG.key}
          data-office-source-document-id={OFFICE_19TH_CENTURY_SCENE_CONFIG.sourceImage.documentId}
        >
          <div className="office-19th-century-windows" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </div>
          <OfficeHandoffPathPoints snapshot={snapshot} employees={employees} onSelect={setSelection} />
          <div className="office-zone-grid" data-appligator-id="office-19th-century-zone-grid">
            {OFFICE_19TH_CENTURY_ZONE_LAYOUTS.map((layout) => (
              <OfficeZone
                key={layout.zone.key}
                layout={layout}
                employees={employees}
                selectedEmployeeId={target && target.employee && target.employee.id}
                onSelect={setSelection}
                sequencer={sequencer}
                movingIds={movingIds}
                delegationHandoffs={delegationHandoffs}
                snapshot={snapshot}
              />
            ))}
          </div>
        </div>
        <OfficeDelegationHandoffLayer handoffs={delegationHandoffs} selectedKey={selectedKey} onSelect={setSelection} />
        {markers.length > 0 && (
          <div className="office-compressed-markers" aria-label="Compressed high-activity markers">
            {markers.map((marker, index) => {
              const bubbleVariant = officeActivityBubbleVariantForWork(marker);
              const bundle = marker.bundle || officeCompressedBundleSummary(marker.group, []);
              const visual = bundle.visual || officeCompressedBundleVisual(marker.category, bubbleVariant.key);
              const promoted = Array.isArray(bundle.promotedStatusKeys) ? bundle.promotedStatusKeys.slice(0, 3) : [];
              const treatment = officeStateTreatment(bundle.statusKey === "completed" ? "completed" : bundle.statusKey);
              return (
                <button
                  type="button"
                  key={marker.id}
                  className={`office-compressed-marker ${workStateClass(marker)} office-preset-${bubbleVariant.key} office-bubble-status-${bubbleVariant.statusTone} office-bundle-${visual.key} office-bundle-status-${bundle.statusKey}`}
                  onClick={() => setSelection({ kind: "compressed", id: compressedSelectionId(marker.employee && marker.employee.id, marker.group, index) })}
                  data-appligator-id={`office-compressed-marker-${marker.id}`}
                  data-appligator-key={marker.id}
                  data-office-bubble-variant={bubbleVariant.key}
                  data-office-bubble-status={bubbleVariant.statusKey}
                  data-office-bundle-visual={visual.key}
                  data-office-bundle-status={bundle.statusKey}
                  data-office-bundle-promoted={promoted.join(",")}
                  data-office-state-treatment={treatment.key}
                  data-office-state-prop={treatment.prop}
                  aria-label={`${marker.count} compressed ${visual.label} ${bundle.statusLabel} events`}
                >
                  <span className="office-bundle-visual" data-appligator-id="office-compressed-bundle-visual" data-appligator-key={marker.id}>
                    <Icon name={visual.icon || bubbleVariant.icon || "stack"} size={13} />
                  </span>
                  <b>{marker.count}</b>
                  <em>{visual.label}</em>
                  <span className="office-bundle-status" data-appligator-id="office-compressed-bundle-status" data-appligator-key={marker.id}>
                    <Icon name={bubbleVariant.statusIcon} size={9} />{bundle.statusLabel}
                  </span>
                  {promoted.map((statusKey) => (
                    <i
                      key={statusKey}
                      className={`office-bundle-promoted-state ${statusKey}`}
                      data-appligator-id="office-compressed-bundle-promoted-state"
                      data-appligator-key={`${marker.id}:${statusKey}`}
                    >
                      {employeeStateLabel(statusKey)}
                    </i>
                  ))}
                </button>
              );
            })}
          </div>
        )}
        <OfficeInspectionDrawer target={target} onOpenActionCenterRun={onOpenActionCenterRun} sequencer={sequencer} />
        <OfficeBottomRail snapshot={snapshot} employees={employees} selectedKey={selectedKey} onSelect={setSelection} sequencer={sequencer} />
      </div>
    );
  }

  function AppligatorOffice({ onToast, onError, onOpenActionCenterRun }) {
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [errorStatus, setErrorStatus] = useState(null);
    const [lastLoadedAt, setLastLoadedAt] = useState(null);
    const [liveState, setLiveState] = useState({ state: "connecting", reason: "Connecting to live Office stream.", fallback: false });
    const refreshTimerRef = useRef(null);
    const mountedRef = useRef(true);
    const snapshotRef = useRef(null);
    const seenOfficeEventIdsRef = useRef(new Set());
    const officeRunClockRef = useRef(new Map());

    const load = useCallback(async (silent, fallbackReason) => {
      if (!window.API.getActionCenterOffice) {
        const message = "Office snapshot endpoint is not available.";
        snapshotRef.current = null;
        setSnapshot(null);
        setError(message);
        setErrorStatus(null);
        setLiveState({ state: "error", reason: message, fallback: false });
        setLoading(false);
        if (onError) onError(new Error(message));
        return false;
      }
      try {
        if (!silent) setLoading(true);
        if (fallbackReason) setLiveState({ state: "fallback", reason: fallbackReason, fallback: true });
        const next = await window.API.getActionCenterOffice();
        if (!mountedRef.current) return false;
        snapshotRef.current = next || null;
        setSnapshot(next || null);
        setError("");
        setErrorStatus(null);
        setLastLoadedAt(new Date());
        officeRunClockRef.current = new Map();
        setLiveState({ state: fallbackReason ? "live" : "snapshot", reason: fallbackReason ? "Snapshot fallback completed; live stream remains subscribed." : "Snapshot loaded.", fallback: false });
        return true;
      } catch (err) {
        if (!mountedRef.current) return;
        const message = (err && err.message) || "Failed to load Office snapshot.";
        snapshotRef.current = null;
        setSnapshot(null);
        setError(message);
        setErrorStatus(err && err.status ? err.status : null);
        setLiveState({ state: permissionDenied(err) ? "denied" : "error", reason: message, fallback: !!fallbackReason });
        if (onError) onError(err);
        return false;
      } finally {
        if (mountedRef.current && !silent) setLoading(false);
      }
    }, [onError]);

    const scheduleRefresh = useCallback((reason) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        load(true, reason || "Live Office stream requested a snapshot fallback.");
      }, 180);
    }, [load]);

    const applyOfficeChange = useCallback((officeChange) => {
      if (!officeChange) return false;
      const eventId = String(officeChange.eventId || "");
      if (!eventId) {
        scheduleRefresh("Live Office event was missing a durable event id.");
        return false;
      }
      if (seenOfficeEventIdsRef.current.has(eventId)) return true;
      seenOfficeEventIdsRef.current.add(eventId);
      if (seenOfficeEventIdsRef.current.size > 500) {
        seenOfficeEventIdsRef.current = new Set(Array.from(seenOfficeEventIdsRef.current).slice(-300));
      }

      const runKey = officeChangeRunKey(officeChange);
      const currentSnapshot = snapshotRef.current;
      const existingWork = currentSnapshot && runKey ? workByRunId(currentSnapshot, runKey) : null;
      if (existingWork && changedAtMillis(latestWorkTime(existingWork)) > changedAtMillis(officeChange.changedAt)) return true;
      if (runKey) {
        const previous = officeRunClockRef.current.get(runKey);
        if (!officeChangeIsNewer(previous, officeChange)) return true;
        officeRunClockRef.current.set(runKey, { version: officeChange.version, changedAt: officeChange.changedAt });
      }

      if (officeChange.requiresSnapshotRefresh || !officeChange.workItem) {
        scheduleRefresh(officeChange.resyncReason || "Live Office event requires snapshot refresh.");
        return true;
      }

      const current = snapshotRef.current;
      if (!current || current.freshness && current.freshness.state === "stale") {
        scheduleRefresh("Local Office snapshot is stale or unavailable.");
        return true;
      }
      const next = mergeOfficeWorkItemIntoSnapshot(current, officeChange);
      if (!next) {
        scheduleRefresh("Live Office delta could not be safely merged.");
        return true;
      }
      snapshotRef.current = next;
      setSnapshot(next);
      setError("");
      setErrorStatus(null);
      setLiveState({ state: "live", reason: "Live Office event merged.", fallback: false });
      return true;
    }, [scheduleRefresh]);

    useEffect(() => {
      mountedRef.current = true;
      load(false);
      return () => {
        mountedRef.current = false;
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        seenOfficeEventIdsRef.current.clear();
        officeRunClockRef.current.clear();
      };
    }, [load]);

    useEffect(() => {
      snapshotRef.current = snapshot;
    }, [snapshot]);

    useEffect(() => {
      if (!window.API.subscribeToChanges) return undefined;
      const unsubscribe = window.API.subscribeToChanges((change) => {
        const officeChange = officeChangeFromChange(change);
        if (officeChange) {
          applyOfficeChange(officeChange);
          return;
        }
        if (isOfficeRefreshChange(change)) scheduleRefresh("Action Center change did not include an Office delta.");
      }, {
        onOpen: () => setLiveState({ state: "live", reason: "Live Office stream connected.", fallback: false }),
        onError: () => {
          setLiveState({ state: "stale", reason: "Live Office stream disconnected or returned invalid data.", fallback: true });
          scheduleRefresh("Live Office stream disconnected or returned invalid data.");
        },
      });
      return () => unsubscribe();
    }, [applyOfficeChange, scheduleRefresh]);

    const freshness = snapshot && snapshot.freshness ? snapshot.freshness : null;
    const streamTone = ["stale", "fallback"].includes(liveState.state) ? "warn" : ["error", "denied"].includes(liveState.state) ? "error" : freshness && freshness.state === "empty" ? "neutral" : "live";
    const freshnessTone = freshness && freshness.state === "stale" ? "warn" : freshness && freshness.state === "empty" ? "neutral" : "live";
    const streamLabel = liveState.state === "live"
      ? `LIVE ${freshness && freshness.lastRunUpdatedAt ? ageLabel(freshness.lastRunUpdatedAt) : ""}`.trim()
      : liveState.state === "fallback"
        ? "FALLBACK"
        : liveState.state === "stale"
          ? "STALE"
          : liveState.state === "error"
            ? "STREAM ERROR"
            : liveState.state === "denied"
              ? "DENIED"
              : liveState.state === "snapshot"
                ? "SNAPSHOT"
                : "CONNECTING";
    const denied = error && permissionDenied({ message: error, status: errorStatus });
    const liveTreatment = officeStateTreatment(liveState.state === "stale" ? "live_disconnected" : liveState.state === "denied" ? "permission_denied_after_load" : liveState.state === "error" ? "failed" : liveState.state === "connecting" ? "loading" : "running");
    const snapshotTreatment = officeStateTreatment(freshness && freshness.state === "stale" ? "stale" : freshness && freshness.state === "empty" ? "empty" : freshness && freshness.state === "loading" ? "loading" : "running");
    const deniedTreatment = officeStateTreatment("permission_denied_after_load");

    return (
      <section className="office-page" data-appligator-id="appligator-office-view">
        <header className="office-topbar">
          <div className="office-title">
            <strong>Appligator Office</strong>
            <span>{scopeLabel(snapshot)} - internal operational debugger</span>
          </div>
          <div className="office-topbar-actions">
            <OfficeBadge tone="scope" icon="globe">{scopeLabel(snapshot)}</OfficeBadge>
            <span
              data-appligator-id="office-live-freshness"
              data-office-state-treatment={liveTreatment.key}
              data-office-state-prop={liveTreatment.prop}
              title={liveState.reason || ""}
            >
              <OfficeBadge tone={streamTone} icon="activity">{streamLabel}</OfficeBadge>
            </span>
            <span
              data-appligator-id="office-snapshot-freshness"
              data-office-state-treatment={snapshotTreatment.key}
              data-office-state-prop={snapshotTreatment.prop}
            >
              <OfficeBadge tone={freshnessTone} icon="clock">{freshness && freshness.state ? `${freshness.state.toUpperCase()} ${ageLabel(freshness.lastRunUpdatedAt || snapshot.generatedAt)}` : "LOADING"}</OfficeBadge>
            </span>
            <OfficeBadge tone="readonly" icon="shield">READ ONLY</OfficeBadge>
            <button
              type="button"
              className="office-refresh"
              data-appligator-id="office-refresh-snapshot"
              title="Refresh Office snapshot"
              aria-label="Refresh Office snapshot"
              onClick={() => load(false).then((refreshed) => { if (refreshed && onToast) onToast("Office snapshot refreshed"); })}
            >
              <Icon name="refresh" size={15} />
            </button>
          </div>
        </header>
        {loading && !snapshot ? (
          <OfficeSkeleton />
        ) : denied ? (
          <div
            className="office-scene-shell denied"
            data-appligator-id="appligator-office-denied"
            data-office-state-treatment={deniedTreatment.key}
            data-office-state-prop={deniedTreatment.prop}
          >
            <OfficeMessageState
              tone="denied"
              icon="shield"
              title="Internal debug access required"
              body="No employee state is shown because this session cannot open the Office snapshot."
            />
          </div>
        ) : error ? (
          <div className="office-scene-shell error" data-appligator-id="appligator-office-unavailable" data-office-state-treatment="failed" data-office-state-prop="broken_machine_error">
            <OfficeMessageState
              tone="error"
              icon="alert"
              title="Office snapshot unavailable"
              body={error}
            />
          </div>
        ) : (
          <OfficeScene snapshot={snapshot} onOpenActionCenterRun={onOpenActionCenterRun} />
        )}
        <footer className="office-statusbar">
          <span>Last loaded: {dateTimeLabel(lastLoadedAt)}</span>
          <span data-appligator-id="office-live-state" data-office-state-treatment={liveTreatment.key}>{liveState.reason}</span>
          <span>No commands exposed</span>
          <span>19th Century sketches: none accepted</span>
        </footer>
      </section>
    );
  }

  window.AppligatorOffice = AppligatorOffice;
})();
